/* eslint-disable no-nested-ternary */
import { useCallback, useEffect, useState } from 'react';
import isEqual from 'lodash.isequal';
import { useForm } from 'react-hook-form';
import { v4 as uuid } from 'uuid';
import {
  BlacklistData,
  BlacklistUserInfoData,
  formatBlacklistItem,
  OnSelectChange,
  PermissionEnum,
  ResponseError,
  responseErrorsResolver,
  SelectItemProps,
  usePrevious,
} from '@ui/src';
import { useAppDispatch } from '@store';
import getRegulatedCompanyQuestionnaireBody from '../helpers/get-regulated-company-questionnaire-body';
import getIndividualRegulatedCompanyQuestionnaireBody from '../helpers/get-individual-regulated-company-questionnarie';
import { CddFormValues } from '../types';
import {
  COFAndIndividualQuestionnarieFormValues,
  BaseInfoFormValues,
  GeneralInfo,
} from '../../../individual/types';
import {
  useApplyCustomerOngoingWarningActionMutation,
  useBatchApplyCustomerOngoingWarningActionMutation,
  useCreateCustomerManualRiskScoreMutation,
  useCreateCustomerUserScoreMutation,
  useGetOngoingMoitoringPdfMutation,
  useLazyGetCustomerAmlUpdatingItemQuery,
  useLazyGetCustomerLastCalculatingScoreQuery,
  useLazyGetCustomerOngoingMonitoringItemQuery,
  useLazyGetCustomerOngoingMonitoringQuery,
  useLazyGetCustomerOngoingWarningQuery,
  useLazyGetCustomersAMLScoresHistoryQuery,
  useLazyGetCustomersChangeLogsQuery,
  useLazyGetCustomerUserScoreQuery,
  useLazyGetCustomerWarningGroupWithPaginationQuery,
  useResetCustomerWarningGroupActionMutation,
  useUpdateCustomerQuestionnaireMutation,
  useUpdateCustomerRangesMutation,
  useLazyGetCustomersRelationShipToCustomerDictionaryQuery,
  useLazyGetCustomersSourceOfWealthForCorporateDictionaryQuery,
  useLazyGetCustomersBusinessActivitiesDictionaryQuery,
  useLazyGetRegulatedCompanyMainClientsDictionaryQuery,
  useLazyGetCustomersSalesChannelsDictionaryQuery,
  useLazyGetCustomersSourceOfFundsQuery,
  useLazyGetCustomersSourceOfWealthQuery,
  useLazyGetCustomersAnnualIncomeDictionaryQuery,
  useLazyGetCustomersAccountsCurrenciesQuery,
  useCreateCustomersAccountsCurrencyMutation,
  useUpdateCustomersAccountsCurrencyMutation,
  useDeleteCustomersAccountsCurrencyMutation,
  useLazyGetCustomersAccountsFlowsQuery,
  useCreateCustomersAccountsFlowMutation,
  useUpdateCustomersAccountsFlowMutation,
  useDeleteCustomersAccountsFlowMutation,
  useLazyGetCustomersExpectedFundsOriginsQuery,
  useCreateCustomersExpectedFundsOriginsMutation,
  useUpdateCustomersExpectedFundsOriginsMutation,
  useDeleteCustomersExptectedFundsOriginsMutation,
  useLazyGetCustomersTypeOfAccountsDictionaryQuery,
  useLazyGetCustomersSourceOfIncomeDictionaryQuery,
  useLazyGetCustomersSourceOfWealthForIndividualDictionaryQuery,
  useLazyGetIndividualCustomersAccountsCurrenciesQuery,
  useCreateIndividualCustomersAccountsCurrencyMutation,
  useUpdateIndividualCustomersAccountsCurrencyMutation,
  useDeleteIndividualCustomersAccountsCurrencyMutation,
  useLazyGetIndividualCustomersAccountsFlowsQuery,
  useCreateIndividualCustomersAccountsFlowMutation,
  useUpdateIndividualCustomersAccountsFlowMutation,
  useDeleteIndividualCustomersAccountsFlowMutation,
  useLazyGetIndividualCustomersExpectedFundsOriginsQuery,
  useCreateIndividualCustomersExpectedFundsOriginsMutation,
  useUpdateIndividualCustomersExpectedFundsOriginsMutation,
  useDeleteIndividualCustomersExptectedFundsOriginsMutation,
  useLazyGetCustomersCRSAndFactaQuery,
  useCreateCustomersCRSAndFactaMutation,
  useUpdateCustomersCRSAndFactaMutation,
  useDeleteCustomersCRSAndFactaMutation,
  useLazyGetCOFCustomersCRSAndFactaQuery,
  useCreateCOFCustomersCRSAndFactaMutation,
  useUpdateCOFCustomersCRSAndFactaMutation,
  useDeleteCOFCustomersCRSAndFactaMutation,
  useLazyGetCustomersAffilatedCompaniesQuery,
  useCreateCustomersAffilatedCompaniesMutation,
  useUpdateCustomersAffilatedCompaniesMutation,
  useDeleteCustomersAffilatedCompaniesMutation,
} from '../../../customers/store/services/customers';
import { useLazyGetCustomersApplicationSchemesQuery } from '../../../../store/dictionaries/services';
import { useUpdateCorporateInfoMutation } from '../../../corporate/store/services/corporate';
import { useUpdateIndividualUserBaseInformationMutation } from '../../../individual/store/services/individual';
import { clearAmlChangeLogs } from '../../store/slices/aml';
import {
  CorporateGeneralInfo,
  CorporateOverviewGeneral,
} from '../../../corporate/types';
import { Employee } from '../../../../store/dictionaries/types';
import { setNotificationVisible } from '../../../../../../../store/slice';
import {
  AccountsCurrencies,
  AccountsCurrenciesFormValues,
  AccountsFlows,
  AccountsFlowsFormValues,
  AffilatedCompany,
  AffilatedCompanyFormValues,
  CRSAndFatca,
  CRSAndFatcaFormValues,
  DictionaryItem,
  ExpectedFundsOrigins,
  ExpectedFundsOriginsFormValues,
} from '../../../new-application/types';
import {
  CustomerRanges,
  CustomerScore,
  CustomerScoreUpdateStatus,
  OrganizationType,
  Questionnarie,
  RiskScoreRange,
  ApplicationScheme,
} from '../../types';

let timeInterval: NodeJS.Timeout;

const useAML = ({
  type,
  customerId,
  baseInformation,
  currentEmployee,
  applicationSchemes,
  amlUpdateStatus,
  activePermissions,
  aml,
  riskScoreRanges,
  isIndividual,
  isCof,
  clientsActivities,
  expectedFundsOrigins,
  accountsCurrencies,
  accountsFlows,
  affiliatedCompanies,
  crsAndFatca,
  relationshipToTheCustomerDictionary,
  sourceOfWealth,
  businessActivities,
  customerMainClients,
  salesChannels,
  customerSourceOfFunds,
  customerSourceOfWealth,
  annualIncomes,
  typeOfAccounts,
  sourceOfIncome,
  sourceOfWealthForIndividual,
}: {
  type: OrganizationType;
  customerId?: string;
  baseInformation?: Partial<GeneralInfo> | Partial<CorporateGeneralInfo>;
  currentEmployee?: Employee;
  applicationSchemes?: ApplicationScheme[];
  amlUpdateStatus?: CustomerScoreUpdateStatus;
  activePermissions?: PermissionEnum[];
  aml?: CustomerScore;
  riskScoreRanges?: RiskScoreRange[];
  isIndividual?: boolean;
  isCof?: boolean;
  clientsActivities?: SelectItemProps[];
  expectedFundsOrigins?: ExpectedFundsOrigins[];
  accountsCurrencies?: AccountsCurrencies[];
  accountsFlows?: AccountsFlows[];
  affiliatedCompanies?: AffilatedCompany[];
  crsAndFatca?: CRSAndFatca[];
  relationshipToTheCustomerDictionary?: DictionaryItem[];
  sourceOfWealth?: DictionaryItem[];
  businessActivities?: DictionaryItem[];
  customerMainClients?: DictionaryItem[];
  salesChannels?: DictionaryItem[];
  customerSourceOfFunds?: DictionaryItem[];
  customerSourceOfWealth?: DictionaryItem[];
  annualIncomes?: DictionaryItem[];
  typeOfAccounts?: DictionaryItem[];
  sourceOfIncome?: DictionaryItem[];
  sourceOfWealthForIndividual?: DictionaryItem[];
}) => {
  const [successMessageVisible, setSuccessMessageVisible] = useState(false);
  const [exportPdfPanelVisible, setExportPdfPanelVisible] = useState(false);
  const [showOngoing, setShowOngoing] = useState(false);
  const [ongoingItemPanelVisible, setOngoingItemPanelVisible] = useState(false);
  const [blacklistDataVisible, setBlacklistDataVisible] = useState(false);
  const [blacklistData, setBlacklistData] = useState<BlacklistData | undefined>(
    undefined,
  );
  const [blacklistItemVisible, setBlacklistItemVisible] = useState(false);
  const [blacklistItem, setBlacklistDataItem] = useState<
    BlacklistUserInfoData | undefined
  >(undefined);
  const [applyError, setApplyError] = useState('');
  const [changeLogsPanelIsOpen, setChangeLogPanelIsOpen] = useState(false);
  const [selectedWarningId, setSelectedWarningId] = useState('');
  const [selectedWarningGroup, setSelectedWarningGroup] = useState('');
  const [totalRiskScore, setTotalRiskScore] = useState(0);
  const [totalRiskScoreLevel, setTotalRiskScoreLevel] = useState('');
  const [manualScoreSidepanelVisible, setManualScoreSidepanelVisible] =
    useState(false);
  const [manualRiskScoreError, setManualRiskScoreError] = useState('');
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [
    riskScoreChangeLogsSidepanelVisible,
    setRiskScoreChangeLogsSidepanelVisible,
  ] = useState(false);
  const [defaultCddValues, setDefaultCddValues] = useState<CddFormValues>({});
  const [cddFormValues, setCddFormValues] = useState<CddFormValues>({});
  const [currentApplicationScheme, setCurrentApplicationScheme] = useState<
    ApplicationScheme | undefined
  >(undefined);
  const [exportDate, setExportDate] = useState<string[]>([]);
  const [defaultQuestionnaire, setDefaultQuestionaire] =
    useState<Questionnarie>();
  const [totalIncomeError, setTotalIncomeError] = useState('');
  const [accountsCurrenciesValues, setAccountsCurrenciesValues] = useState<
    AccountsCurrenciesFormValues[]
  >([
    {
      id: 'accounts-currency-1',
      accountOpeningPurpose: '',
      currencyId: '',
      plannedTransactionNature: '',
    },
  ]);
  const [selectedAccountsCurrencyId, setSelectedAccountsCurrencyId] =
    useState('');
  const [accountsFlowsValues, setAccountsFlowsValues] = useState<
    AccountsFlowsFormValues[]
  >([]);
  const [expectedFundsOriginsValues, setExpectedFundsOriginsValues] = useState<
    ExpectedFundsOriginsFormValues[]
  >([
    {
      id: 'expected-funds-origins-1',
      currencyId: '',
      mainCounterpartiesAndWebsites: '',
      countryOfIncorporationId: '',
      counterpartyBankCountryIds: [],
      activityCountryIds: [],
      mainActivities: '',
      transactionReason: '',
      transactionType: 'INCOMING',
    },
    {
      id: 'expected-funds-origins-2',
      currencyId: '',
      mainCounterpartiesAndWebsites: '',
      countryOfIncorporationId: '',
      counterpartyBankCountryIds: [],
      activityCountryIds: [],
      mainActivities: '',
      transactionReason: '',
      transactionType: 'OUTGOING',
    },
  ]);
  const [selectedExpectedFundsOriginsId, setSelectedFundsOriginsId] =
    useState('');
  const [crsAndFatcaValues, setCrsAndFatcaValues] = useState<
    CRSAndFatcaFormValues[]
  >([
    {
      id: 'crs-and-fatca-1',
      residenceCountryId: '',
      taxIdentificationNumber: '',
      noTinReason: '',
    },
  ]);
  const [selectedCrsAndFatcaId, setSelectedCrsAndFatcaId] = useState('');
  const [affilatedCompaniesValues, setAffilatedCompaniesValues] = useState<
    AffilatedCompanyFormValues[]
  >([
    {
      id: 'affilated-company-1',
      name: '',
      registrationNumber: '',
      mainActivities: '',
      position: '',
      countryId: '',
      percentageOfOwnership: '',
    },
  ]);
  const [selectedAffilatedCompanyId, setSelectedAffilatedCompanyId] =
    useState('');

  const [defaultOrganizationValues, setDefaultOrganizationValues] =
    useState<Partial<BaseInfoFormValues>>();

  const employmentActivity = clientsActivities?.find(
    (item) => item.name === 'Employment',
  );
  const otherActivity = clientsActivities?.find(
    (item) => item.name === 'Other',
  );
  const businessOwnerActivity = clientsActivities?.find(
    (item) => item.name === 'Business Owner',
  );

  const isDirty = !isEqual(defaultCddValues, cddFormValues);

  const dispatch = useAppDispatch();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
    clearErrors,
    setError,
  } = useForm<Questionnarie>();

  const {
    control: organizationControl,
    reset: organizationReset,
    formState: { errors: organizationErrors },
    watch: organizationWatch,
    getValues,
    setError: organizationSetError,
    clearErrors: organizationClearErrors,
    setValue: organizationSetValue,
  } = useForm<Partial<BaseInfoFormValues>>();

  const [getCustomerScore, { isLoading }] = useLazyGetCustomerUserScoreQuery();
  const [getCustomerAmlUpdatingItem] = useLazyGetCustomerAmlUpdatingItemQuery();
  const [getCustomerLastCalculaingScore] =
    useLazyGetCustomerLastCalculatingScoreQuery();
  const [createCustomerScore, { isSuccess, isLoading: requestInProgress }] =
    useCreateCustomerUserScoreMutation();
  const [
    getCustomerOngoingMonitoring,
    { isLoading: ongoingIsLoading, isFetching: ongoingIsFetching },
  ] = useLazyGetCustomerOngoingMonitoringQuery();
  const [
    getCustomerOngoingMonitoringItem,
    { isLoading: ongoingItemIsLoading, isFetching: ongoingItemIsFetching },
  ] = useLazyGetCustomerOngoingMonitoringItemQuery();
  const [getApplicationsSchemes] = useLazyGetCustomersApplicationSchemesQuery();
  const [updateQuestionnaire, { isLoading: questionnnaireRequestInProgress }] =
    useUpdateCustomerQuestionnaireMutation();
  const [applyCustomerOngoingWarning, { isLoading: applyWarningInProgress }] =
    useApplyCustomerOngoingWarningActionMutation();
  const [
    batchApplyCustomerOngoingWarning,
    {
      isLoading: batchApplyWarningInProgress,
      isSuccess: batchApplyWarningIsSuccess,
    },
  ] = useBatchApplyCustomerOngoingWarningActionMutation();
  const [
    getOngoingMonitoringPdf,
    {
      isLoading: ongoingMonitoringPdfIsLoading,
      isSuccess: ongoingMonitoringPdfIsSuccess,
    },
  ] = useGetOngoingMoitoringPdfMutation();
  const [
    getChangeLogs,
    { isLoading: changeLogsIsLoading, isFetching: changeLogsIsFetching },
  ] = useLazyGetCustomersChangeLogsQuery();
  const [
    resetCustomerTransactionWarning,
    {
      isLoading: resetTransactionsWarningIsLoading,
      isSuccess: resetTransactionsWarningIsSuccess,
    },
  ] = useResetCustomerWarningGroupActionMutation();
  const [
    getCustomerWarning,
    {
      isLoading: paginationInProgress,
      isFetching: paginationIsFetching,
      isSuccess: paginationIsSuccess,
    },
  ] = useLazyGetCustomerWarningGroupWithPaginationQuery();
  const [
    getCustomerOngoingWarningById,
    { isLoading: isWarningByIdLoading, isFetching: isWarningByIdFetching },
  ] = useLazyGetCustomerOngoingWarningQuery();
  const [
    getCustomerAmlScoresHistory,
    {
      isLoading: amlScoresHistoryIsLoading,
      isFetching: amlScoresHistoryIsFetching,
    },
  ] = useLazyGetCustomersAMLScoresHistoryQuery();
  const [updateCustomerRanges, { isLoading: updateRangesInProgress }] =
    useUpdateCustomerRangesMutation();
  const [
    createCustomerManualRiskScore,
    {
      isLoading: createRiskScoreIsLoading,
      isSuccess: createRiskScoreIsSuccess,
    },
  ] = useCreateCustomerManualRiskScoreMutation();
  const [updateCorporateInfo, { isLoading: updateInfoIsLoading }] =
    useUpdateCorporateInfoMutation();
  const [updateIndividualInformation, { isLoading: informationIsUpdate }] =
    useUpdateIndividualUserBaseInformationMutation();
  const [getRelationShipToCustomerDictionary] =
    useLazyGetCustomersRelationShipToCustomerDictionaryQuery();
  const [getSourceOfWealthForCorporateDictionary] =
    useLazyGetCustomersSourceOfWealthForCorporateDictionaryQuery();
  const [getBusinessActivitiesDictionary] =
    useLazyGetCustomersBusinessActivitiesDictionaryQuery();
  const [getRegulatedCompanyMainClientsDictionary] =
    useLazyGetRegulatedCompanyMainClientsDictionaryQuery();
  const [getSalesChannelsDictionary] =
    useLazyGetCustomersSalesChannelsDictionaryQuery();
  const [getCustomersSourceOfFundsDictionary] =
    useLazyGetCustomersSourceOfFundsQuery();
  const [getCustomersSourceOfWealth] = useLazyGetCustomersSourceOfWealthQuery();
  const [getCustomersAnnualIncomes] =
    useLazyGetCustomersAnnualIncomeDictionaryQuery();
  const [getCustomersTypeOfAccounts] =
    useLazyGetCustomersTypeOfAccountsDictionaryQuery();

  const [
    getAccountsCurrencies,
    {
      isLoading: accountsCurrenciesIsLoading,
      isFetching: accountsCurrenciesIsFetching,
    },
  ] = useLazyGetCustomersAccountsCurrenciesQuery();
  const [
    createAccountsCurrencies,
    { isLoading: createAccountsCurrenciesIsProgress },
  ] = useCreateCustomersAccountsCurrencyMutation();
  const [
    updateAccountsCurrencies,
    { isLoading: updateAccountsCurrenciesInProgress },
  ] = useUpdateCustomersAccountsCurrencyMutation();
  const [
    deleteAccountsCurrencies,
    {
      isLoading: deleteAccountsCurrenciesInProgress,
      isSuccess: deleteAccountsCurrenciesIsSuccess,
    },
  ] = useDeleteCustomersAccountsCurrencyMutation();
  const [
    getAccountsFlows,
    { isLoading: accountsFlowsIsLoading, isFetching: accountsFlowsIsFetching },
  ] = useLazyGetCustomersAccountsFlowsQuery();
  const [createAccountsFlows, { isLoading: createAccountsFlowsIsProgress }] =
    useCreateCustomersAccountsFlowMutation();
  const [updateAccountsFlows, { isLoading: updateAccountsFlowsInProgress }] =
    useUpdateCustomersAccountsFlowMutation();
  const [deleteAccountsFlows] = useDeleteCustomersAccountsFlowMutation();
  const [
    getExpectedFundsOrigins,
    {
      isLoading: expectedFundsOriginsIsLoading,
      isFetching: expectedFundsOriginsIsFetching,
    },
  ] = useLazyGetCustomersExpectedFundsOriginsQuery();
  const [
    createExpectedFundsOrigins,
    { isLoading: createExpectedFundsOriginsInProgress },
  ] = useCreateCustomersExpectedFundsOriginsMutation();
  const [
    updateExpectedFundsOrigins,
    { isLoading: updateExpectedFundsOriginsInProgress },
  ] = useUpdateCustomersExpectedFundsOriginsMutation();
  const [
    deleteExpectedFundsOrigins,
    {
      isLoading: deleteExpectedFundsOriginsInProgress,
      isSuccess: deleteExpectedFundsOriginsIsSuccess,
    },
  ] = useDeleteCustomersExptectedFundsOriginsMutation();
  const [getCustomersSourceOfIncome] =
    useLazyGetCustomersSourceOfIncomeDictionaryQuery();
  const [getCustomersSourceOfWealthForIndividua] =
    useLazyGetCustomersSourceOfWealthForIndividualDictionaryQuery();
  const [
    getIndividualAccountsCurrencies,
    {
      isLoading: accountsIndividualCurrenciesIsLoading,
      isFetching: accountsIndividualCurrenciesIsFetching,
    },
  ] = useLazyGetIndividualCustomersAccountsCurrenciesQuery();
  const [
    createIndividualAccountsCurrencies,
    { isLoading: createIndividualAccountsCurrenciesIsProgress },
  ] = useCreateIndividualCustomersAccountsCurrencyMutation();
  const [
    updateIndividualAccountsCurrencies,
    { isLoading: updateIndividualAccountsCurrenciesInProgress },
  ] = useUpdateIndividualCustomersAccountsCurrencyMutation();
  const [
    deleteIndividualAccountsCurrencies,
    {
      isLoading: deleteIndividualAccountsCurrenciesInProgress,
      isSuccess: deleteIndividualAccountsCurrenciesIsSuccess,
    },
  ] = useDeleteIndividualCustomersAccountsCurrencyMutation();
  const [
    getIndividualAccountsFlows,
    {
      isLoading: accountsIndividualFlowsIsLoading,
      isFetching: accountsIndividualFlowsIsFetching,
    },
  ] = useLazyGetIndividualCustomersAccountsFlowsQuery();
  const [
    createIndividualAccountsFlows,
    { isLoading: createIndividualAccountsFlowsIsProgress },
  ] = useCreateIndividualCustomersAccountsFlowMutation();
  const [
    updateIndividualAccountsFlows,
    { isLoading: updateIndividualAccountsFlowsInProgress },
  ] = useUpdateIndividualCustomersAccountsFlowMutation();
  const [deleteIndividualAccountsFlows] =
    useDeleteIndividualCustomersAccountsFlowMutation();
  const [
    getIndividualExpectedFundsOrigins,
    {
      isLoading: expectedIndividualFundsOriginsIsLoading,
      isFetching: expectedIndividualFundsOriginsIsFetching,
    },
  ] = useLazyGetIndividualCustomersExpectedFundsOriginsQuery();
  const [
    createIndividualExpectedFundsOrigins,
    { isLoading: createIndividualExpectedFundsOriginsInProgress },
  ] = useCreateIndividualCustomersExpectedFundsOriginsMutation();
  const [
    updateIndividualExpectedFundsOrigins,
    { isLoading: updateIndividualExpectedFundsOriginsInProgress },
  ] = useUpdateIndividualCustomersExpectedFundsOriginsMutation();
  const [
    deleteIndividualExpectedFundsOrigins,
    {
      isLoading: deleteIndividualExpectedFundsOriginsInProgress,
      isSuccess: deleteIndividualExpectedFundsOriginsIsSuccess,
    },
  ] = useDeleteIndividualCustomersExptectedFundsOriginsMutation();
  const [
    getCOFCrsAndFatca,
    {
      isLoading: cofCrsAndFatcaIsLoading,
      isFetching: cofCrsAndFatcaIsFetching,
    },
  ] = useLazyGetCOFCustomersCRSAndFactaQuery();
  const [createCOFCrsAndFatca, { isLoading: createCofCrsAndFatcaIsProgress }] =
    useCreateCOFCustomersCRSAndFactaMutation();
  const [updateCOFCrsAndFatca, { isLoading: updateCofCrsAndFatcaInProgress }] =
    useUpdateCOFCustomersCRSAndFactaMutation();
  const [
    deleteCOFCrsAndFatca,
    {
      isLoading: deleteCofCrsAndFatcaInProgress,
      isSuccess: deleteCofCrsAndFatcaIsSuccess,
    },
  ] = useDeleteCOFCustomersCRSAndFactaMutation();
  const [
    getCrsAndFatca,
    { isLoading: crsAndFatcaIsLoading, isFetching: crsAndFatcaIsFetching },
  ] = useLazyGetCustomersCRSAndFactaQuery();
  const [createCrsAndFatca, { isLoading: createCrsAndFatcaIsProgress }] =
    useCreateCustomersCRSAndFactaMutation();
  const [updateCrsAndFatca, { isLoading: updateCrsAndFatcaInProgress }] =
    useUpdateCustomersCRSAndFactaMutation();
  const [
    deleteCrsAndFatca,
    {
      isLoading: deleteCrsAndFatcaInProgress,
      isSuccess: deleteCrsAndFatcaIsSuccess,
    },
  ] = useDeleteCustomersCRSAndFactaMutation();
  const [
    getAffilatedCompanies,
    {
      isLoading: affilatedCompaniesIsLoading,
      isFetching: affilatedCompaniesIsFetching,
    },
  ] = useLazyGetCustomersAffilatedCompaniesQuery();
  const [
    createAffilatedCompany,
    { isLoading: createAffilatedCompanyInProgress },
  ] = useCreateCustomersAffilatedCompaniesMutation();
  const [
    updateAffilatedCompany,
    { isLoading: updateAffilatedCompanyInProgress },
  ] = useUpdateCustomersAffilatedCompaniesMutation();
  const [
    deleteAffilatedCompany,
    {
      isLoading: deleteAffilatedCompanyInProgress,
      isSuccess: deleteAffilatedCompanyIsSuccess,
    },
  ] = useDeleteCustomersAffilatedCompaniesMutation();

  const questionnaireFields = watch();
  const organizationFields = organizationWatch();

  const prevClientActivitiy = usePrevious(organizationFields.clientActivityId);

  const onResetQuestionnaire = () => {
    reset(defaultQuestionnaire);
    organizationReset(defaultOrganizationValues);

    if (accountsCurrencies) {
      setAccountsCurrenciesValues(
        accountsCurrencies?.map((item) => ({
          id: item.id,
          currencyId: item.currencyId,
          accountOpeningPurpose: item.accountOpeningPurpose,
          plannedTransactionNature: item.plannedTransactionNature,
        })),
      );
    }

    if (accountsFlows) {
      setAccountsFlowsValues(
        accountsFlows?.map((item) => ({
          id: item.id,
          transactionType: item.transactionType,
          currencyId: item.currencyId,
          expectedAnnualCreditTurnover:
            item.expectedAnnualCreditTurnover.toString(),
          maxTransferAmountPerTransaction:
            item.maxTransferAmountPerTransaction.toString(),
          transactionFrequency: item.transactionFrequency.toString(),
          transactionFrequencyPeriodMonths:
            item.transactionFrequencyPeriodMonths.toString(),
        })),
      );
    }

    if (expectedFundsOrigins) {
      setExpectedFundsOriginsValues(
        expectedFundsOrigins?.map((item) => ({
          id: item.id,
          currencyId: item.currencyId,
          mainCounterpartiesAndWebsites: item.mainCounterpartiesAndWebsites,
          countryOfIncorporationId: item.countryOfIncorporationId,
          counterpartyBankCountryIds: item.counterpartyBankCountryIds,
          activityCountryIds: item.activityCountryIds,
          mainActivities: item.mainActivities,
          transactionReason: item.transactionReason,
          transactionType: item.transactionType,
        })),
      );
    }

    if (crsAndFatca) {
      setCrsAndFatcaValues(
        crsAndFatca?.map((item) => ({
          id: item.id,
          residenceCountryId: item?.residenceCountryId,
          taxIdentificationNumber: item?.taxIdentificationNumber || '',
          noTinReason: item?.noTinReason || '',
        })),
      );
    }

    if (affiliatedCompanies) {
      setAffilatedCompaniesValues(
        affiliatedCompanies?.map((item) => ({
          id: item.id,
          name: item.name,
          registrationNumber: item.registrationNumber,
          mainActivities: item.mainActivities,
          position: item.position,
          countryId: item.countryId,
          percentageOfOwnership: item.percentageOfOwnership?.toString(),
        })),
      );
    }
  };

  const onSaveExpectedFundsOrigins = async () => {
    const expectedFundsOriginsForCreation: ExpectedFundsOriginsFormValues[] =
      [];
    const expectedFundsOriginsForUpdate: ExpectedFundsOriginsFormValues[] = [];

    expectedFundsOriginsValues?.forEach((item) => {
      if (item.id?.includes('expected-funds-origins')) {
        expectedFundsOriginsForCreation.push(item);
      } else {
        const currentExpectedFundsOrigins = expectedFundsOrigins?.find(
          (value) => value.id === item.id,
        );

        if (currentExpectedFundsOrigins) {
          if (
            !isEqual(item, {
              id: currentExpectedFundsOrigins.id,
              currencyId: currentExpectedFundsOrigins.currencyId,
              mainCounterpartiesAndWebsites:
                currentExpectedFundsOrigins.mainCounterpartiesAndWebsites,
              countryOfIncorporationId:
                currentExpectedFundsOrigins.countryOfIncorporationId,
              counterpartyBankCountryIds:
                currentExpectedFundsOrigins.counterpartyBankCountryIds,
              activityCountryIds:
                currentExpectedFundsOrigins.activityCountryIds,
              mainActivities: currentExpectedFundsOrigins.mainActivities,
              transactionReason: currentExpectedFundsOrigins.transactionReason,
              transactionType: currentExpectedFundsOrigins.transactionType,
            })
          ) {
            expectedFundsOriginsForUpdate.push(item);
          }
        }
      }
    });

    if (expectedFundsOriginsForCreation?.length) {
      await Promise.all(
        expectedFundsOriginsForCreation
          ?.filter((item) => item.currencyId)
          ?.map(async (item) => {
            let response;

            if (isIndividual) {
              response = await createIndividualExpectedFundsOrigins({
                id: baseInformation?.questionnaire?.id as string,
                body: {
                  currencyId: item.currencyId,
                  mainCounterpartiesAndWebsites:
                    item.mainCounterpartiesAndWebsites || '',
                  countryOfIncorporationId: item.countryOfIncorporationId || '',
                  counterpartyBankCountryIds:
                    item.counterpartyBankCountryIds || '',
                  activityCountryIds: item.activityCountryIds || '',
                  mainActivities: item.mainActivities || '',
                  transactionReason: item.transactionReason || '',
                  transactionType: item.transactionType || '',
                },
              });
            } else {
              response = await createExpectedFundsOrigins({
                id: baseInformation?.questionnaire?.id as string,
                body: {
                  currencyId: item.currencyId,
                  mainCounterpartiesAndWebsites:
                    item.mainCounterpartiesAndWebsites || '',
                  countryOfIncorporationId: item.countryOfIncorporationId || '',
                  counterpartyBankCountryIds:
                    item.counterpartyBankCountryIds || '',
                  activityCountryIds: item.activityCountryIds || '',
                  mainActivities: item.mainActivities || '',
                  transactionReason: item.transactionReason || '',
                  transactionType: item.transactionType || '',
                },
              });
            }

            return response;
          }),
      );
    }

    if (expectedFundsOriginsForUpdate?.length) {
      await Promise.all(
        expectedFundsOriginsForUpdate?.map(async (item) => {
          let response;

          if (isIndividual) {
            response = await updateIndividualExpectedFundsOrigins({
              id: item.id as string,
              body: {
                currencyId: item.currencyId,
                mainCounterpartiesAndWebsites:
                  item.mainCounterpartiesAndWebsites || '',
                countryOfIncorporationId: item.countryOfIncorporationId || '',
                counterpartyBankCountryIds:
                  item.counterpartyBankCountryIds || '',
                activityCountryIds: item.activityCountryIds || '',
                mainActivities: item.mainActivities || '',
                transactionReason: item.transactionReason || '',
                transactionType: item.transactionType || '',
              },
            });
          } else {
            response = await updateExpectedFundsOrigins({
              id: item.id as string,
              body: {
                currencyId: item.currencyId,
                mainCounterpartiesAndWebsites:
                  item.mainCounterpartiesAndWebsites || '',
                countryOfIncorporationId: item.countryOfIncorporationId || '',
                counterpartyBankCountryIds:
                  item.counterpartyBankCountryIds || '',
                activityCountryIds: item.activityCountryIds || '',
                mainActivities: item.mainActivities || '',
                transactionReason: item.transactionReason || '',
                transactionType: item.transactionType || '',
              },
            });
          }

          return response;
        }),
      );
    }
  };

  const onSaveAccountsCurrencies = async () => {
    const accountsCurrenciesForCreation: AccountsCurrenciesFormValues[] = [];
    const accountsCurrenciesForUpdate: AccountsCurrenciesFormValues[] = [];

    accountsCurrenciesValues?.forEach((item) => {
      if (item.id?.includes('accounts-currency')) {
        accountsCurrenciesForCreation.push(item);
      } else {
        const currentAccountsCurrency = accountsCurrencies?.find(
          (currency) => currency.id === item.id,
        );

        if (currentAccountsCurrency) {
          if (
            !isEqual(item, {
              id: currentAccountsCurrency.id,
              currencyId: currentAccountsCurrency.currencyId,
              accountOpeningPurpose:
                currentAccountsCurrency.accountOpeningPurpose,
              plannedTransactionNature:
                currentAccountsCurrency.plannedTransactionNature,
            })
          ) {
            accountsCurrenciesForUpdate.push(item);
          }
        }
      }
    });

    if (accountsCurrenciesForCreation?.length) {
      await Promise.all(
        accountsCurrenciesForCreation
          ?.filter((item) => item.currencyId)
          ?.map(async (item) => {
            let response;

            if (isIndividual) {
              response = await createIndividualAccountsCurrencies({
                id: baseInformation?.questionnaire?.id as string,
                body: {
                  currencyId: item.currencyId,
                  accountOpeningPurpose: item.accountOpeningPurpose || '',
                  plannedTransactionNature: item.plannedTransactionNature || '',
                },
              });
            } else {
              response = await createAccountsCurrencies({
                id: baseInformation?.questionnaire?.id as string,
                body: {
                  currencyId: item.currencyId,
                  accountOpeningPurpose: item.accountOpeningPurpose || '',
                  plannedTransactionNature: item.plannedTransactionNature || '',
                },
              });
            }

            return response;
          }),
      );
    }

    if (accountsCurrenciesForUpdate?.length) {
      await Promise.all(
        accountsCurrenciesForUpdate?.map(async (item) => {
          let response;

          if (isIndividual) {
            response = await updateIndividualAccountsCurrencies({
              id: item.id as string,
              body: {
                currencyId: item.currencyId,
                accountOpeningPurpose: item.accountOpeningPurpose || '',
                plannedTransactionNature: item.plannedTransactionNature || '',
              },
            });
          } else {
            response = await updateAccountsCurrencies({
              id: item.id as string,
              body: {
                currencyId: item.currencyId,
                accountOpeningPurpose: item.accountOpeningPurpose || '',
                plannedTransactionNature: item.plannedTransactionNature || '',
              },
            });
          }

          return response;
        }),
      );
    }
  };

  const onSaveAccountsFlows = async () => {
    const accountsFlowsForCreation: AccountsFlowsFormValues[] = [];
    const accountsFlowsForUpdate: AccountsFlowsFormValues[] = [];

    accountsFlowsValues?.forEach((item) => {
      if (item.id?.includes('accounts-flow')) {
        accountsFlowsForCreation.push(item);
      } else {
        const currentAccountsFlow = accountsFlows?.find(
          (flow) => flow.id === item.id,
        );

        if (currentAccountsFlow) {
          if (
            !isEqual(item, {
              id: currentAccountsFlow.id,
              transactionType: currentAccountsFlow.transactionType,
              currencyId: currentAccountsFlow.currencyId,
              expectedAnnualCreditTurnover:
                currentAccountsFlow.expectedAnnualCreditTurnover.toString(),
              maxTransferAmountPerTransaction:
                currentAccountsFlow.maxTransferAmountPerTransaction.toString(),
              transactionFrequency:
                currentAccountsFlow.transactionFrequency.toString(),
              transactionFrequencyPeriodMonths:
                currentAccountsFlow.transactionFrequencyPeriodMonths.toString(),
            })
          ) {
            accountsFlowsForUpdate.push(item);
          }
        }
      }
    });

    if (
      accountsFlowsForCreation?.filter(
        (item) =>
          item.expectedAnnualCreditTurnover &&
          item.maxTransferAmountPerTransaction &&
          item.transactionFrequency &&
          item.transactionFrequencyPeriodMonths,
      )?.length
    ) {
      await Promise.all(
        accountsFlowsForCreation
          ?.filter(
            (item) =>
              item.expectedAnnualCreditTurnover &&
              item.maxTransferAmountPerTransaction &&
              item.transactionFrequency &&
              item.transactionFrequencyPeriodMonths,
          )
          ?.map(async (item) => {
            let response;

            if (isIndividual) {
              response = await createIndividualAccountsFlows({
                id: baseInformation?.questionnaire?.id as string,
                body: {
                  transactionType: item.transactionType,
                  currencyId: item.currencyId,
                  expectedAnnualCreditTurnover: Number(
                    item.expectedAnnualCreditTurnover,
                  ),
                  maxTransferAmountPerTransaction: Number(
                    item.maxTransferAmountPerTransaction,
                  ),
                  transactionFrequency: Number(item.transactionFrequency),
                  transactionFrequencyPeriodMonths: Number(
                    item.transactionFrequencyPeriodMonths,
                  ),
                },
              });
            } else {
              response = await createAccountsFlows({
                id: baseInformation?.questionnaire?.id as string,
                body: {
                  transactionType: item.transactionType,
                  currencyId: item.currencyId,
                  expectedAnnualCreditTurnover: Number(
                    item.expectedAnnualCreditTurnover,
                  ),
                  maxTransferAmountPerTransaction: Number(
                    item.maxTransferAmountPerTransaction,
                  ),
                  transactionFrequency: Number(item.transactionFrequency),
                  transactionFrequencyPeriodMonths: Number(
                    item.transactionFrequencyPeriodMonths,
                  ),
                },
              });
            }

            return response;
          }),
      );
    }

    if (accountsFlowsForUpdate?.length) {
      await Promise.all(
        accountsFlowsForUpdate?.map(async (item) => {
          let response;

          if (isIndividual) {
            response = await updateIndividualAccountsFlows({
              id: item.id as string,
              body: {
                transactionType: item.transactionType,
                currencyId: item.currencyId,
                expectedAnnualCreditTurnover: Number(
                  item.expectedAnnualCreditTurnover,
                ),
                maxTransferAmountPerTransaction: Number(
                  item.maxTransferAmountPerTransaction,
                ),
                transactionFrequency: Number(item.transactionFrequency),
                transactionFrequencyPeriodMonths: Number(
                  item.transactionFrequencyPeriodMonths,
                ),
              },
            });
          } else {
            response = await updateAccountsFlows({
              id: item.id as string,
              body: {
                transactionType: item.transactionType,
                currencyId: item.currencyId,
                expectedAnnualCreditTurnover: Number(
                  item.expectedAnnualCreditTurnover,
                ),
                maxTransferAmountPerTransaction: Number(
                  item.maxTransferAmountPerTransaction,
                ),
                transactionFrequency: Number(item.transactionFrequency),
                transactionFrequencyPeriodMonths: Number(
                  item.transactionFrequencyPeriodMonths,
                ),
              },
            });
          }

          return response;
        }),
      );
    }
  };

  const onSaveAffilatedCompanies = async () => {
    const affilatedCompaniesForCreate: AffilatedCompanyFormValues[] = [];
    const affilatedCompaniesForForUpdate: AffilatedCompanyFormValues[] = [];

    affilatedCompaniesValues?.forEach((item) => {
      if (item.id?.includes('affilated-company')) {
        affilatedCompaniesForCreate.push(item);
      } else {
        const currentAffilatedCompany = affiliatedCompanies?.find(
          (value) => value.id === item.id,
        );

        if (currentAffilatedCompany) {
          if (
            !isEqual(item, {
              id: currentAffilatedCompany.id,
              name: currentAffilatedCompany.name,
              registrationNumber: currentAffilatedCompany.registrationNumber,
              mainActivities: currentAffilatedCompany.mainActivities,
              position: currentAffilatedCompany.position,
              countryId: currentAffilatedCompany.countryId,
              percentageOfOwnership:
                currentAffilatedCompany.percentageOfOwnership,
            })
          ) {
            affilatedCompaniesForForUpdate.push(item);
          }
        }
      }
    });

    if (affilatedCompaniesForCreate?.length) {
      await Promise.all(
        affilatedCompaniesForCreate
          ?.filter(
            (item) =>
              item.name &&
              item.registrationNumber &&
              item.mainActivities &&
              item.position &&
              item.countryId &&
              item.percentageOfOwnership,
          )
          ?.map(async (item) => {
            const response = await createAffilatedCompany({
              id: baseInformation?.questionnaire?.id as string,
              body: {
                name: item.name,
                registrationNumber: item.registrationNumber,
                mainActivities: item.mainActivities,
                position: item.position,
                countryId: item.countryId,
                percentageOfOwnership: Number(item.percentageOfOwnership),
              },
            });

            return response;
          }),
      );
    }

    if (affilatedCompaniesForForUpdate?.length) {
      await Promise.all(
        affilatedCompaniesForForUpdate?.map(async (item) => {
          const response = await updateAffilatedCompany({
            id: item.id as string,
            body: {
              name: item.name,
              registrationNumber: item.registrationNumber,
              mainActivities: item.mainActivities,
              position: item.position,
              countryId: item.countryId,
              percentageOfOwnership: Number(item.percentageOfOwnership),
            },
          });

          return response;
        }),
      );
    }
  };

  const onSaveCRSAndFatca = async () => {
    const crsAndFatcaForCreation: CRSAndFatcaFormValues[] = [];
    const crsAndFatcaForForUpdate: CRSAndFatcaFormValues[] = [];

    crsAndFatcaValues?.forEach((item) => {
      if (item.id?.includes('crs-and-fatca')) {
        crsAndFatcaForCreation.push(item);
      } else {
        const currentcrsAndFatca = crsAndFatca?.find(
          (value) => value.id === item.id,
        );

        if (currentcrsAndFatca) {
          if (
            !isEqual(item, {
              id: currentcrsAndFatca.id,
              residenceCountryId: currentcrsAndFatca?.residenceCountryId,
              taxIdentificationNumber:
                currentcrsAndFatca?.taxIdentificationNumber,
              noTinReason: currentcrsAndFatca?.noTinReason,
            })
          ) {
            crsAndFatcaForForUpdate.push(item);
          }
        }
      }
    });

    if (crsAndFatcaForCreation?.length) {
      await Promise.all(
        crsAndFatcaForCreation
          ?.filter((item) => item.residenceCountryId)
          ?.map(async (item) => {
            let response;

            if (!isCof) {
              response = await createCrsAndFatca({
                id: baseInformation?.questionnaire?.id as string,
                body: {
                  residenceCountryId: item.residenceCountryId,
                  taxIdentificationNumber: item.taxIdentificationNumber,
                  noTinReason: item.noTinReason,
                },
              });
            } else if (isCof) {
              response = await createCOFCrsAndFatca({
                id: baseInformation?.questionnaire?.id as string,
                body: {
                  residenceCountryId: item.residenceCountryId,
                  taxIdentificationNumber: item.taxIdentificationNumber,
                  noTinReason: item.noTinReason,
                },
              });
            }

            return response;
          }),
      );
    }

    if (crsAndFatcaForForUpdate?.length) {
      await Promise.all(
        crsAndFatcaForForUpdate?.map(async (item) => {
          let response;

          if (!isCof) {
            response = await updateCrsAndFatca({
              id: item.id as string,
              body: {
                residenceCountryId: item.residenceCountryId,
                taxIdentificationNumber: item.taxIdentificationNumber,
                noTinReason: item.noTinReason,
              },
            });
          } else if (isCof) {
            response = await updateCOFCrsAndFatca({
              id: item.id as string,
              body: {
                residenceCountryId: item.residenceCountryId,
                taxIdentificationNumber: item.taxIdentificationNumber,
                noTinReason: item.noTinReason,
              },
            });
          }

          return response;
        }),
      );
    }
  };

  const onDeleteAccountCurrencies = async (id: string) => {
    if (id.includes('accounts-currency')) {
      setAccountsCurrenciesValues(
        accountsCurrenciesValues?.filter((item) => item.id !== id),
      );

      const currentAccountsCurrency = accountsCurrenciesValues?.find(
        (item) => item.id === id,
      );

      if (currentAccountsCurrency) {
        const currentAccountsFlows = accountsFlowsValues?.filter(
          (item) => item.currencyId === currentAccountsCurrency?.currencyId,
        );

        const currentExpectedFundsOrigins = expectedFundsOriginsValues?.filter(
          (item) => item.currencyId === currentAccountsCurrency?.currencyId,
        );

        if (currentAccountsFlows?.length) {
          setAccountsFlowsValues(
            accountsFlowsValues?.filter((item) =>
              currentAccountsFlows.every((flow) => item.id !== flow.id),
            ),
          );
        }

        if (currentExpectedFundsOrigins?.length) {
          setExpectedFundsOriginsValues(
            expectedFundsOriginsValues?.filter((item) =>
              currentExpectedFundsOrigins.every(
                (expectedFund) => item.id !== expectedFund.id,
              ),
            ),
          );
        }
      }
    } else {
      setSelectedAccountsCurrencyId(id);

      const currentAccountsCurrency = accountsCurrenciesValues?.find(
        (item) => item.id === id,
      );

      if (currentAccountsCurrency) {
        const currentAccountsFlows = accountsFlowsValues?.filter(
          (item) => item.currencyId === currentAccountsCurrency?.currencyId,
        );

        const currentExpectedFundsOrigins = expectedFundsOriginsValues?.filter(
          (item) => item.currencyId === currentAccountsCurrency?.currencyId,
        );

        if (currentAccountsFlows?.length) {
          await Promise.all(
            currentAccountsFlows
              ?.filter((item) => !item.id?.includes('accounts-flow'))
              ?.map(async (item) => {
                let response;

                if (isIndividual) {
                  response = await deleteIndividualAccountsFlows(
                    item.id as string,
                  );
                } else {
                  response = await deleteAccountsFlows(item.id as string);
                }

                return response;
              }),
          );

          if (
            currentAccountsFlows?.filter((item) =>
              item.id?.includes('accounts-flow'),
            ).length
          ) {
            const notCreatedAccountsFlow = currentAccountsFlows?.filter(
              (item) => item.id?.includes('accounts-flow'),
            );

            setAccountsFlowsValues(
              accountsFlowsValues?.filter((item) =>
                notCreatedAccountsFlow.every((flow) => item.id !== flow.id),
              ),
            );
          }
        }

        if (currentExpectedFundsOrigins?.length) {
          await Promise.all(
            currentExpectedFundsOrigins
              ?.filter((item) => !item.id?.includes('expected-funds-origins'))
              ?.map(async (item) => {
                let response;

                if (isIndividual) {
                  response = await deleteIndividualExpectedFundsOrigins(
                    item.id as string,
                  );
                } else {
                  response = await deleteExpectedFundsOrigins(
                    item.id as string,
                  );
                }

                return response;
              }),
          );

          if (
            currentExpectedFundsOrigins?.filter((item) =>
              item.id?.includes('expected-funds-origins'),
            )?.length
          ) {
            const notCreatedExpectedFundsOrigins =
              currentExpectedFundsOrigins?.filter((item) =>
                item.id?.includes('expected-funds-origins'),
              );

            setExpectedFundsOriginsValues(
              expectedFundsOriginsValues?.filter((item) =>
                notCreatedExpectedFundsOrigins.every(
                  (expectedFundsOrigin) => item.id !== expectedFundsOrigin.id,
                ),
              ),
            );
          }
        }
      }

      if (isIndividual) {
        await deleteIndividualAccountsCurrencies(id);
      } else {
        await deleteAccountsCurrencies(id);
      }
    }
  };

  const onDeleteExpectedFundsOrigins = async (id: string) => {
    if (id.includes('expected-funds-origins')) {
      setExpectedFundsOriginsValues(
        expectedFundsOriginsValues?.filter((item) => item.id !== id),
      );
    } else {
      setSelectedFundsOriginsId(id);

      if (isIndividual) {
        await deleteIndividualExpectedFundsOrigins(id);
      } else {
        await deleteExpectedFundsOrigins(id);
      }
    }
  };

  const onDeleteAffilatedCompany = async (id: string) => {
    if (id.includes('affilated-company')) {
      setAffilatedCompaniesValues(
        affilatedCompaniesValues?.filter((item) => item.id !== id),
      );
    } else {
      setSelectedAffilatedCompanyId(id);

      await deleteAffilatedCompany(id);
    }
  };

  const onDeleteCRSAndFatca = async (id: string) => {
    if (id.includes('crs-and-fatca')) {
      setCrsAndFatcaValues(crsAndFatcaValues?.filter((item) => item.id !== id));
    } else {
      setSelectedCrsAndFatcaId(id);

      if (!isCof) {
        await deleteCrsAndFatca(id);
      } else if (isCof) {
        await deleteCOFCrsAndFatca(id);
      }
    }
  };

  const handleCloseAmlChangeLogsPanel = () => {
    setRiskScoreChangeLogsSidepanelVisible(false);
    dispatch(clearAmlChangeLogs());
  };

  const getCustomerAmlChangeLogsAction = async (cursor?: string) => {
    await getCustomerAmlScoresHistory({
      id: customerId as string,
      query: `?take=40${cursor ? `&cursor=${cursor}` : ''}`,
    });
  };

  const createCustomerManualRiskScoreAction = async () => {
    setManualRiskScoreError('');

    const response = (await createCustomerManualRiskScore({
      id: customerId as string,
      totalRiskScore,
    })) as { error?: ResponseError };

    if (response?.error) {
      responseErrorsResolver({
        errors: response.error,
        setError: () => {},
        setGlobalError: setManualRiskScoreError,
      });
    }
  };

  const getCustomerOngoingWarningByIdAction = async ({
    id,
    warningId,
  }: {
    id: string;
    warningId: string;
  }) => {
    const response = await getCustomerOngoingWarningById({
      orgId: customerId as string,
      warningId,
      id,
    });

    if (
      !blacklistData &&
      !blacklistItem &&
      !blacklistDataVisible &&
      !blacklistItemVisible &&
      response
    ) {
      if (response?.data?.data?.blacklistDoc) {
        setBlacklistData(response?.data?.data?.blacklistDoc);
        setBlacklistDataVisible(true);
      } else if (response?.data?.data?.blacklistItem) {
        setBlacklistDataItem(
          formatBlacklistItem(response?.data?.data?.blacklistItem),
        );
        setBlacklistItemVisible(true);
      }
    }
  };

  const getChangeLogsAction = useCallback(
    (cursor?: string) => {
      getChangeLogs(
        `?take=20&entityType=ORGANIZATION&entityId=${customerId}${
          cursor ? `&cursor=${cursor}` : ''
        }`,
      );
    },
    [customerId],
  );

  const getOngoingMonitoringPdfAction = async () => {
    const body: {
      createdDateFrom?: string;
      createdDateTo?: string;
    } = {};

    if (exportDate && exportDate.length) {
      body.createdDateFrom = new Date(exportDate[0]).toISOString();

      if (exportDate.length > 1) {
        body.createdDateTo = new Date(exportDate[1])?.toISOString();
      }
    }

    await getOngoingMonitoringPdf({
      id: customerId as string,
      body,
    });
  };

  const getCustomerWarningsAction = async ({
    warningGroupId,
    cursor,
  }: {
    warningGroupId: string;
    cursor: string;
  }) => {
    setSelectedWarningGroup(warningGroupId);

    await getCustomerWarning({
      id: customerId as string,
      warningGroupId,
      cursor,
    });
  };

  const getCustomerLasCalculaingScoreAction = useCallback(
    async (amlUpdateId: string) => {
      const { data } = await getCustomerAmlUpdatingItem({
        orgId: customerId as string,
        id: amlUpdateId,
      });

      if (
        data?.data.status === CustomerScoreUpdateStatus.FAIL ||
        data?.data.status === CustomerScoreUpdateStatus.SUCCESS
      ) {
        clearInterval(timeInterval);
      }

      if (data?.data.status === CustomerScoreUpdateStatus.SUCCESS) {
        getCustomerScore(customerId as string);
      }
    },
    [],
  );

  const getCustomerScoreAction = useCallback(async () => {
    (await getCustomerScore(customerId as string)) as {
      data: { data?: CustomerScore };
      error?: ResponseError;
    };
  }, [customerId]);

  const onUpdateRange = (ranges: Record<string, string>) => {
    const updatedRanges: CustomerRanges = {};

    if (
      ranges.monthlyMaxValueIncomingPaymentRangeId !==
      baseInformation?.monthlyMaxValueIncomingPaymentRangeId
    ) {
      updatedRanges.monthlyMaxValueIncomingPaymentRangeId =
        ranges.monthlyMaxValueIncomingPaymentRangeId;
    }

    if (
      ranges.monthlyMaxValueOutgoingPaymentRangeId !==
      baseInformation?.monthlyMaxValueOutgoingPaymentRangeId
    ) {
      updatedRanges.monthlyMaxValueOutgoingPaymentRangeId =
        ranges.monthlyMaxValueOutgoingPaymentRangeId;
    }

    if (
      ranges.monthlyNumberIncomingTransactionsRangeId !==
      baseInformation?.monthlyNumberIncomingTransactionsRangeId
    ) {
      updatedRanges.monthlyNumberIncomingTransactionsRangeId =
        ranges.monthlyNumberIncomingTransactionsRangeId;
    }

    if (
      ranges.monthlyNumberOutgoingTransactionsRangeId !==
      baseInformation?.monthlyNumberOutgoingTransactionsRangeId
    ) {
      updatedRanges.monthlyNumberOutgoingTransactionsRangeId =
        ranges.monthlyNumberOutgoingTransactionsRangeId;
    }

    if (
      ranges.monthlyTurnoverRangeId !== baseInformation?.monthlyTurnoverRangeId
    ) {
      updatedRanges.monthlyTurnoverRangeId = ranges.monthlyTurnoverRangeId;
    }

    if (
      ranges.monthlyValueIncomingTransactionsRangeId !==
      baseInformation?.monthlyValueIncomingTransactionsRangeId
    ) {
      updatedRanges.monthlyValueIncomingTransactionsRangeId =
        ranges.monthlyValueIncomingTransactionsRangeId;
    }

    if (
      ranges.monthlyValueOutgoingTransactionsRangeId !==
      baseInformation?.monthlyValueOutgoingTransactionsRangeId
    ) {
      updatedRanges.monthlyValueOutgoingTransactionsRangeId =
        ranges.monthlyValueOutgoingTransactionsRangeId;
    }

    if (Object.keys(updatedRanges)?.length > 0) {
      updateCustomerRanges({
        id: customerId as string,
        body: updatedRanges,
      });
    }
  };

  const onUpdateCorporateInfo = (values: Partial<CorporateOverviewGeneral>) => {
    const body: Partial<CorporateOverviewGeneral> = {};

    const amlCtfComplianceProgram = Boolean(
      values.amlCtfComplianceProgram === 'yes',
    );

    if (
      values.purposeOfAccount !==
      (baseInformation as Partial<CorporateGeneralInfo>)?.purposeOfAccount
    ) {
      body.purposeOfAccount = values.purposeOfAccount;
    }

    if (
      !isEqual(
        values.expectedTypeOfClients,
        (baseInformation as Partial<CorporateGeneralInfo>)
          ?.expectedTypeOfClients,
      )
    ) {
      body.expectedTypeOfClients = values.expectedTypeOfClients;
    }

    if (
      !isEqual(
        values.requiredCurrenciesCryptoId,
        (baseInformation as Partial<CorporateGeneralInfo>)
          ?.requiredCurrenciesCryptoId,
      )
    ) {
      body.requiredCurrenciesCryptoId = values.requiredCurrenciesCryptoId;
    }

    if (
      amlCtfComplianceProgram !==
      (baseInformation as Partial<CorporateGeneralInfo>)
        ?.amlCtfComplianceProgram
    ) {
      body.amlCtfComplianceProgram = amlCtfComplianceProgram;
    }

    if (Object.keys(body)?.length > 0) {
      updateCorporateInfo({
        id: customerId as string,
        body,
      });
    }
  };

  const onSaveChanges = () => {
    onUpdateRange({
      monthlyMaxValueIncomingPaymentRangeId:
        cddFormValues?.monthlyMaxValueIncomingPaymentRangeId as string,
      monthlyMaxValueOutgoingPaymentRangeId:
        cddFormValues?.monthlyMaxValueOutgoingPaymentRangeId as string,
      monthlyNumberIncomingTransactionsRangeId:
        cddFormValues?.monthlyNumberIncomingTransactionsRangeId as string,
      monthlyNumberOutgoingTransactionsRangeId:
        cddFormValues?.monthlyNumberOutgoingTransactionsRangeId as string,
      monthlyTurnoverRangeId: cddFormValues?.monthlyTurnoverRangeId as string,
      monthlyValueIncomingTransactionsRangeId:
        cddFormValues?.monthlyValueIncomingTransactionsRangeId as string,
      monthlyValueOutgoingTransactionsRangeId:
        cddFormValues?.monthlyValueOutgoingTransactionsRangeId as string,
    });

    if (
      currentApplicationScheme?.purposeOfAccountActive ||
      currentApplicationScheme?.amlCtfComplianceProgramActive ||
      currentApplicationScheme?.expectedTypeOfClientsActive ||
      currentApplicationScheme?.requiredCurrenciesCryptoActive
    ) {
      onUpdateCorporateInfo({
        purposeOfAccount: cddFormValues?.purposeOfAccount,
        expectedTypeOfClients: cddFormValues?.expectedTypeOfClients,
        requiredCurrenciesCryptoId: cddFormValues?.requiredCurrenciesCryptoId,
        amlCtfComplianceProgram: cddFormValues?.amlCtfComplianceProgram,
      });
    }
  };

  const applyCustomerOngoingWarningAction = async ({
    id,
    warningId,
    action,
    comment,
    matchStatus,
  }: {
    id: string;
    warningId: string;
    action: string;
    comment: string;
    matchStatus?: string;
  }) => {
    setApplyError('');

    const response = (await applyCustomerOngoingWarning({
      orgId: customerId as string,
      id,
      warningId,
      body: {
        action,
        comment,
        employeeId: currentEmployee?.id as string,
        matchStatus,
      },
    })) as { error: ResponseError };

    if (response.error) {
      if (response.error.data.message) {
        if (
          Array.isArray(response.error.data.message) &&
          response.error.data.message[0].property === 'coreError'
        ) {
          setApplyError(response.error.data.message[0].value as string);
        } else if (typeof response.error.data.message === 'string') {
          setApplyError(response.error.data.message);
        } else {
          setApplyError('Internal Server Error. Try Again');
        }
      }
    }
  };

  const batchApplyCustomerOngoingWarningAction = async ({
    id,
    action,
    comment,
    matchStatus,
  }: {
    id: string;
    action: string;
    comment: string;
    matchStatus?: string;
  }) => {
    setApplyError('');

    const response = (await batchApplyCustomerOngoingWarning({
      orgId: customerId as string,
      body: {
        warningGroupId: id,
        action,
        employeeId: currentEmployee?.id as string,
        comment,
        matchStatus,
      },
    })) as { error: ResponseError };

    if (response.error) {
      if (response.error.data.message) {
        if (
          Array.isArray(response.error.data.message) &&
          response.error.data.message[0].property === 'coreError'
        ) {
          setApplyError(response.error.data.message[0].value as string);
        } else if (typeof response.error.data.message === 'string') {
          setApplyError(response.error.data.message);
        } else {
          setApplyError('Internal Server Error. Try Again');
        }
      }
    }
  };

  const onResetTransactionWarningAction = async (
    warningId: string,
    id: string,
  ) => {
    setSelectedWarningId(warningId);

    const response = (await resetCustomerTransactionWarning({
      orgId: customerId as string,
      id,
      warningId,
    })) as { error: ResponseError };

    if (response.error) {
      if (response.error.data.message) {
        if (
          Array.isArray(response.error.data.message) &&
          response.error.data.message[0].property === 'coreError'
        ) {
          setApplyError(response.error.data.message[0].value as string);
        } else if (typeof response.error.data.message === 'string') {
          setApplyError(response.error.data.message);
        } else {
          setApplyError('Internal Server Error. Try Again');
        }
      }
    }
  };

  const onCDDFormValuesChange = (
    e: React.ChangeEvent<HTMLInputElement> | OnSelectChange,
  ) => {
    const { name, value } = e.target;

    const nextCddFormValues = { ...cddFormValues };

    // @ts-ignore
    nextCddFormValues[name] = value;

    setCddFormValues(nextCddFormValues);
  };

  const onOngoingRowClick = (id: string) => {
    setOngoingItemPanelVisible(true);

    getCustomerOngoingMonitoringItem({ orgId: customerId as string, id });
  };

  const onUpdate = useCallback(() => {
    createCustomerScore(customerId as string);
  }, [customerId]);

  const onUpdateQuestionnaire = handleSubmit(async (values) => {
    let body: Questionnarie = {};

    if (type === OrganizationType.CORPORATE) {
      if (
        currentApplicationScheme?.id === 'cdbb8c71-90e6-4a5e-964f-e55169e0c4df'
      ) {
        const questionnarieBody = getRegulatedCompanyQuestionnaireBody({
          questionnarieValues: values,
        });

        body = {
          ...body,
          ...questionnarieBody,
        };

        onSaveAccountsCurrencies();
        onSaveAccountsFlows();
        onSaveExpectedFundsOrigins();
      } else {
        if (values.typeOfBusinessLicence) {
          body.typeOfBusinessLicence = values.typeOfBusinessLicence;
        }

        if (values.hmrcMlrNumber) {
          body.hmrcMlrNumber = values.hmrcMlrNumber;
        }

        if (values.hmrcRegistrationExpiryDate) {
          body.hmrcRegistrationExpiryDate = values.hmrcRegistrationExpiryDate;
        }

        if (values.fcaNumber) {
          body.fcaNumber = values.fcaNumber;
        }

        if (values.icoNumber) {
          body.icoNumber = values.icoNumber;
        }

        if (values.numberOfBranches) {
          body.numberOfBranches = values.numberOfBranches;
        }

        if (values.numberOfAgents) {
          body.numberOfAgents = values.numberOfAgents;
        }

        if (values.mlroDetails) {
          body.mlroDetails = values.mlroDetails;
        }

        if (values.complianceConsultantDetails) {
          body.complianceConsultantDetails = values.complianceConsultantDetails;
        }

        if (values.accountantDetails) {
          body.accountantDetails = values.accountantDetails;
        }

        if (values.itProviderDetails) {
          body.itProviderDetails = values.itProviderDetails;
        }

        if (values.payoutPartnerDetails) {
          body.payoutPartnerDetails = values.payoutPartnerDetails;
        }

        if (values.transactionMonitoringKind) {
          body.transactionMonitoringKind = values.transactionMonitoringKind;
        }

        if (values.screeningKind) {
          body.screeningKind = values.screeningKind;
        }

        if (values.regulatoryInvestigationDetails) {
          body.regulatoryInvestigationDetails =
            values.regulatoryInvestigationDetails;
        }

        if (values.visitedByAuthorities) {
          body.visitedByAuthorities = values.visitedByAuthorities;
        }

        if (values.visitedByAuthoritiesDetails) {
          body.visitedByAuthoritiesDetails = values.visitedByAuthoritiesDetails;
        }

        if (values.changesInManagementOrOwnershipDetails) {
          body.changesInManagementOrOwnershipDetails =
            values.changesInManagementOrOwnershipDetails;
        }

        if (values.workingWithAnyOtherIpspDetails) {
          body.workingWithAnyOtherIpspDetails =
            values.workingWithAnyOtherIpspDetails;
        }

        if (values.auditedByExternalComplianceDetails) {
          body.auditedByExternalComplianceDetails =
            values.auditedByExternalComplianceDetails;
        }

        body.hasTransactionMonitoring = Boolean(
          values.hasTransactionMonitoring === 'yes',
        );
        body.hasScreening = Boolean(values.hasScreening === 'yes');
        body.hasRegulatoryInvestigation = Boolean(
          values.hasRegulatoryInvestigation === 'yes',
        );
        body.hasOnboardCorporateClients = Boolean(
          values.hasOnboardCorporateClients === 'yes',
        );
        body.hasOnboardMsb = Boolean(values.hasOnboardMsb === 'yes');
        body.hasPaymentsForCharities = Boolean(
          values.hasPaymentsForCharities === 'yes',
        );
        body.hasChangesInManagementOrOwnership = Boolean(
          values.hasChangesInManagementOrOwnership === 'yes',
        );
        body.hasWorkingWithAnyOtherIpsp = Boolean(
          values.hasWorkingWithAnyOtherIpsp === 'yes',
        );
        body.hasAuditedByExternalCompliance = Boolean(
          values.hasAuditedByExternalCompliance === 'yes',
        );
      }
    } else {
      // eslint-disable-next-line no-lonely-if
      if (
        currentApplicationScheme?.id ===
          'ed560559-8354-4634-b6dd-eb341dfb551a' ||
        currentApplicationScheme?.id === 'a87db4b4-3c78-4564-bba1-c16a19553159'
      ) {
        const questionnarieBody =
          getIndividualRegulatedCompanyQuestionnaireBody({
            questionnarieValues:
              values as COFAndIndividualQuestionnarieFormValues,
          });

        body = {
          ...body,
          ...questionnarieBody,
        } as Questionnarie;

        if (!isCof) {
          onSaveAccountsCurrencies();
          onSaveAccountsFlows();
          onSaveExpectedFundsOrigins();
        }

        if (isCof) {
          onSaveAffilatedCompanies();
        }

        onSaveCRSAndFatca();

        if (!isEqual(defaultOrganizationValues, organizationFields)) {
          const organizationValues = getValues();

          const organizationResponse = (await updateIndividualInformation({
            id: baseInformation?.id as string,
            body: {
              ...organizationValues,
              isUSResident: Boolean(organizationValues?.isUSResident === 'yes'),
            } as BaseInfoFormValues,
          })) as { error?: ResponseError };

          if (organizationResponse?.error) {
            responseErrorsResolver({
              errors: organizationResponse?.error,
              setError: organizationSetError,
            });
          }
        }
      }
    }

    const response = (await updateQuestionnaire({
      id: customerId as string,
      body,
    })) as { error?: ResponseError };

    if (response?.error) {
      responseErrorsResolver({
        errors: response?.error,
        setError,
      });
    }
  });

  useEffect(() => {
    if (baseInformation) {
      const body: CddFormValues = {
        monthlyMaxValueIncomingPaymentRangeId:
          baseInformation?.monthlyMaxValueIncomingPaymentRangeId,
        monthlyMaxValueOutgoingPaymentRangeId:
          baseInformation?.monthlyMaxValueOutgoingPaymentRangeId,
        monthlyNumberIncomingTransactionsRangeId:
          baseInformation?.monthlyNumberIncomingTransactionsRangeId,
        monthlyNumberOutgoingTransactionsRangeId:
          baseInformation?.monthlyNumberOutgoingTransactionsRangeId,
        monthlyTurnoverRangeId: baseInformation?.monthlyTurnoverRangeId,
        monthlyValueIncomingTransactionsRangeId:
          baseInformation?.monthlyValueIncomingTransactionsRangeId,
        monthlyValueOutgoingTransactionsRangeId:
          baseInformation?.monthlyValueOutgoingTransactionsRangeId,
      };

      if (type === OrganizationType.CORPORATE) {
        if (
          currentApplicationScheme?.purposeOfAccountActive ||
          currentApplicationScheme?.amlCtfComplianceProgramActive ||
          currentApplicationScheme?.expectedTypeOfClientsActive ||
          currentApplicationScheme?.requiredCurrenciesCryptoActive
        ) {
          if (currentApplicationScheme?.purposeOfAccountActive) {
            body.purposeOfAccount = (
              baseInformation as Partial<CorporateGeneralInfo>
            )?.purposeOfAccount;
          }

          if (currentApplicationScheme?.amlCtfComplianceProgramActive) {
            body.amlCtfComplianceProgram =
              (baseInformation as Partial<CorporateGeneralInfo>)
                ?.amlCtfComplianceProgram !== null &&
              (baseInformation as Partial<CorporateGeneralInfo>)
                ?.amlCtfComplianceProgram
                ? 'yes'
                : 'no';
          }
        }

        if (currentApplicationScheme?.expectedTypeOfClientsActive) {
          body.expectedTypeOfClients = (
            baseInformation as Partial<CorporateGeneralInfo>
          )?.expectedTypeOfClients;
        }

        if (currentApplicationScheme?.requiredCurrenciesCryptoActive) {
          body.requiredCurrenciesCryptoId = (
            baseInformation as Partial<CorporateGeneralInfo>
          )?.requiredCurrenciesCryptoId;
        }
      }

      setDefaultCddValues(body);
      setCddFormValues(body);
    }
  }, [baseInformation, currentApplicationScheme]);

  useEffect(() => {
    if (!applicationSchemes) {
      getApplicationsSchemes();
    }
  }, [applicationSchemes]);

  useEffect(() => {
    if (paginationIsSuccess) {
      setSelectedWarningGroup('');
    }
  }, [paginationIsSuccess]);

  useEffect(() => {
    if (createRiskScoreIsSuccess) {
      setManualRiskScoreError('');
      setTotalRiskScore(0);
      setTotalRiskScoreLevel('');
      setManualScoreSidepanelVisible(false);
      getCustomerScoreAction();
    }
  }, [createRiskScoreIsSuccess]);

  useEffect(() => {
    if (
      amlUpdateStatus &&
      amlUpdateStatus === CustomerScoreUpdateStatus.SUCCESS &&
      activePermissions?.includes(
        PermissionEnum.INDIVIDUAL_CUSTOMERS_SCORE_LIST,
      )
    ) {
      const requestIsAvailable =
        type === OrganizationType.INDIVIDUAL
          ? Boolean(
              activePermissions?.includes(
                PermissionEnum.INDIVIDUAL_CUSTOMERS_SCORE_LIST,
              ),
            )
          : Boolean(
              activePermissions?.includes(
                PermissionEnum.CORPORATE_CUSTOMERS_SCORE_LIST,
              ),
            );

      if (requestIsAvailable) {
        getCustomerLastCalculaingScore(customerId as string);
      }
    }
  }, [amlUpdateStatus, activePermissions, type]);

  useEffect(() => {
    if (
      changeLogsPanelIsOpen &&
      activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST)
    ) {
      getChangeLogsAction();
    }
  }, [changeLogsPanelIsOpen]);

  useEffect(() => {
    if (customerId) {
      if (
        (type === OrganizationType.INDIVIDUAL &&
          activePermissions?.includes(
            PermissionEnum.INDIVIDUAL_CUSTOMERS_SCORE_LIST,
          )) ||
        (type === OrganizationType.CORPORATE &&
          activePermissions?.includes(
            PermissionEnum.CORPORATE_CUSTOMERS_SCORE_LIST,
          ))
      ) {
        getCustomerScoreAction();
      }

      if (
        (type === OrganizationType.INDIVIDUAL &&
          (baseInformation as Partial<GeneralInfo>)?.type !==
            OrganizationType.COMPANY_OFFICIAL &&
          activePermissions?.includes(
            PermissionEnum.INDIVIDUAL_CUSTOMERS_AML_LIST,
          )) ||
        (type === OrganizationType.CORPORATE &&
          activePermissions?.includes(
            PermissionEnum.CORPORATE_CUSTOMERS_AML_LIST,
          ))
      ) {
        getCustomerOngoingMonitoring({ id: customerId });
      }
    }
  }, [customerId, activePermissions]);

  useEffect(() => {
    if (
      amlUpdateStatus &&
      ((type === OrganizationType.INDIVIDUAL &&
        activePermissions?.includes(
          PermissionEnum.INDIVIDUAL_CUSTOMERS_SCORE_LIST,
        )) ||
        (type === OrganizationType.CORPORATE &&
          activePermissions?.includes(
            PermissionEnum.CORPORATE_CUSTOMERS_SCORE_LIST,
          )))
    ) {
      const amlUpdateId = localStorage.getItem('amlUpdateId');

      if (amlUpdateId) {
        timeInterval = setInterval(() => {
          getCustomerLasCalculaingScoreAction(amlUpdateId);
        }, 15000);
      }
    }
  }, [amlUpdateStatus, activePermissions]);

  useEffect(() => {
    if (isSuccess && aml) {
      setSuccessMessageVisible(true);

      setTimeout(() => {
        setSuccessMessageVisible(false);
      }, 6000);
    }
  }, [isSuccess]);

  useEffect(() => {
    if (aml && riskScoreRanges && manualScoreSidepanelVisible) {
      setTotalRiskScore(aml?.totalRiskScore);

      setTotalRiskScoreLevel(
        riskScoreRanges?.find(
          (item) =>
            aml?.totalRiskScore >= item.rangeStart &&
            aml?.totalRiskScore <= item.rangeEnd,
        )?.id as string,
      );
    }
  }, [aml, riskScoreRanges, manualScoreSidepanelVisible]);

  useEffect(() => {
    if (ongoingMonitoringPdfIsSuccess) {
      setExportDate([]);
      dispatch(
        setNotificationVisible({
          visible: true,
          text: "Report in progress. You'll be notified.",
          type: 'client-outgoing-monitoring',
        }),
      );
      setExportPdfPanelVisible(false);
    }
  }, [ongoingMonitoringPdfIsSuccess]);

  useEffect(() => {
    if (
      customerId &&
      ((showOngoing &&
        ((type === OrganizationType.INDIVIDUAL &&
          activePermissions?.includes(
            PermissionEnum.INDIVIDUAL_CUSTOMERS_AML_LIST,
          )) ||
          (type === OrganizationType.CORPORATE &&
            activePermissions?.includes(
              PermissionEnum.CORPORATE_CUSTOMERS_AML_LIST,
            )))) ||
        ((baseInformation as Partial<GeneralInfo>)?.type ===
          OrganizationType.COMPANY_OFFICIAL &&
          activePermissions?.includes(
            PermissionEnum.INDIVIDUAL_CUSTOMERS_AML_LIST,
          )))
    ) {
      getCustomerOngoingMonitoring({ id: customerId, take: '40' });
    }
  }, [customerId, showOngoing]);

  useEffect(() => {
    if (baseInformation && baseInformation.clientScheme && applicationSchemes) {
      const currentScheme = applicationSchemes?.find(
        (item) =>
          item.name ===
          (baseInformation as Partial<CorporateGeneralInfo>).clientScheme?.name,
      );

      if (currentScheme) {
        setCurrentApplicationScheme(currentScheme);
      }
    }
  }, [baseInformation, applicationSchemes]);

  useEffect(() => {
    if (accountsCurrencies) {
      setAccountsCurrenciesValues(
        accountsCurrencies?.map((item) => ({
          id: item.id,
          currencyId: item.currencyId,
          accountOpeningPurpose: item.accountOpeningPurpose,
          plannedTransactionNature: item.plannedTransactionNature,
        })),
      );
    }
  }, [accountsCurrencies]);

  useEffect(() => {
    if (accountsFlows && accountsCurrencies) {
      const nextAccountsFlows = accountsFlows?.map((item) => ({
        id: item.id,
        transactionType: item.transactionType,
        currencyId: item.currencyId,
        expectedAnnualCreditTurnover:
          item.expectedAnnualCreditTurnover.toString(),
        maxTransferAmountPerTransaction:
          item.maxTransferAmountPerTransaction.toString(),
        transactionFrequency: item.transactionFrequency.toString(),
        transactionFrequencyPeriodMonths:
          item.transactionFrequencyPeriodMonths.toString(),
      }));

      const notCreatedCurrencies: AccountsCurrencies[] = [];

      accountsCurrencies?.forEach((item) => {
        if (
          !nextAccountsFlows?.find(
            (flow) => flow.currencyId === item.currencyId,
          )
        ) {
          notCreatedCurrencies.push(item);
        }
      });

      if (notCreatedCurrencies?.length) {
        notCreatedCurrencies?.forEach((item) => {
          nextAccountsFlows.push(
            {
              id: `accounts-flow-${uuid()}`,
              transactionType: 'INCOMING',
              currencyId: item.currencyId,
              expectedAnnualCreditTurnover: '',
              maxTransferAmountPerTransaction: '',
              transactionFrequency: '',
              transactionFrequencyPeriodMonths: '',
            },
            {
              id: `accounts-flow-${uuid()}`,
              transactionType: 'OUTGOING',
              currencyId: item.currencyId,
              expectedAnnualCreditTurnover: '',
              maxTransferAmountPerTransaction: '',
              transactionFrequency: '',
              transactionFrequencyPeriodMonths: '',
            },
          );
        });
      }

      setAccountsFlowsValues(nextAccountsFlows);
    }
  }, [accountsFlows, accountsCurrencies]);

  useEffect(() => {
    if (expectedFundsOrigins) {
      setExpectedFundsOriginsValues(
        expectedFundsOrigins?.map((item) => ({
          id: item.id,
          currencyId: item.currencyId,
          mainCounterpartiesAndWebsites: item.mainCounterpartiesAndWebsites,
          countryOfIncorporationId: item.countryOfIncorporationId,
          counterpartyBankCountryIds: item.counterpartyBankCountryIds,
          activityCountryIds: item.activityCountryIds,
          mainActivities: item.mainActivities,
          transactionReason: item.transactionReason,
          transactionType: item.transactionType,
        })),
      );
    }
  }, [expectedFundsOrigins]);

  useEffect(() => {
    if (
      prevClientActivitiy &&
      prevClientActivitiy === employmentActivity?.value &&
      organizationFields.clientActivityId !== employmentActivity?.value
    ) {
      organizationSetValue('nameOfEmployer', '');
      organizationSetValue('countryOfEmployerId', '');
      organizationSetValue('employeePosition', '');
      setValue('natureOfBusinessActivity', '');
    }

    if (
      prevClientActivitiy &&
      prevClientActivitiy === businessOwnerActivity?.value &&
      organizationFields.clientActivityId !== businessOwnerActivity?.value
    ) {
      organizationSetValue('countryOfEmployerId', '');
      setValue('nameOfCompany', '');
      setValue('ownership', '');
      setValue('natureOfBusinessActivity', '');
    }

    if (
      prevClientActivitiy &&
      prevClientActivitiy ===
        clientsActivities?.find((item) => item.name === 'Self-employed')
          ?.value &&
      organizationFields.clientActivityId !==
        clientsActivities?.find((item) => item.name === 'Self-employed')?.value
    ) {
      setValue('natureOfBusinessActivity', '');
    }

    if (
      prevClientActivitiy &&
      prevClientActivitiy === otherActivity?.value &&
      organizationFields.clientActivityId !== otherActivity?.value
    ) {
      organizationSetValue('typeOfActivityDescription', '');
    }
  }, [organizationFields.clientActivityId, prevClientActivitiy]);

  useEffect(() => {
    if (affiliatedCompanies) {
      setAffilatedCompaniesValues(
        affiliatedCompanies?.map((item) => ({
          id: item.id,
          name: item.name,
          registrationNumber: item.registrationNumber,
          mainActivities: item.mainActivities,
          position: item.position,
          countryId: item.countryId,
          percentageOfOwnership: item.percentageOfOwnership?.toString(),
        })),
      );
    }
  }, [affiliatedCompanies]);

  useEffect(() => {
    if (crsAndFatca) {
      setCrsAndFatcaValues(
        crsAndFatca?.map((item) => ({
          id: item.id,
          residenceCountryId: item?.residenceCountryId,
          taxIdentificationNumber: item?.taxIdentificationNumber || '',
          noTinReason: item?.noTinReason || '',
        })),
      );
    }
  }, [crsAndFatca]);

  useEffect(() => {
    if (deleteAffilatedCompanyIsSuccess) {
      setSelectedAffilatedCompanyId('');
    }
  }, [deleteAffilatedCompanyIsSuccess]);

  useEffect(() => {
    if (
      deleteAccountsCurrenciesIsSuccess ||
      deleteIndividualAccountsCurrenciesIsSuccess
    ) {
      setSelectedAccountsCurrencyId('');
    }
  }, [
    deleteAccountsCurrenciesIsSuccess,
    deleteIndividualAccountsCurrenciesIsSuccess,
  ]);

  useEffect(() => {
    if (
      deleteExpectedFundsOriginsIsSuccess ||
      deleteIndividualExpectedFundsOriginsIsSuccess
    ) {
      setSelectedFundsOriginsId('');
    }
  }, [
    deleteExpectedFundsOriginsIsSuccess,
    deleteIndividualExpectedFundsOriginsIsSuccess,
  ]);

  useEffect(() => {
    if (deleteCrsAndFatcaIsSuccess || deleteCofCrsAndFatcaIsSuccess) {
      setSelectedCrsAndFatcaId('');
    }
  }, [deleteCrsAndFatcaIsSuccess, deleteCofCrsAndFatcaIsSuccess]);

  useEffect(() => {
    if (
      baseInformation &&
      baseInformation?.questionnaire &&
      currentApplicationScheme &&
      Boolean(
        currentApplicationScheme?.id === 'ed560559-8354-4634-b6dd-eb341dfb551a',
      )
    ) {
      if (!accountsCurrencies) {
        getIndividualAccountsCurrencies(
          baseInformation?.questionnaire?.id as string,
        );
      }

      if (!accountsFlows) {
        getIndividualAccountsFlows(
          baseInformation?.questionnaire?.id as string,
        );
      }

      if (!expectedFundsOrigins) {
        getIndividualExpectedFundsOrigins(
          baseInformation?.questionnaire?.id as string,
        );
      }

      if (!crsAndFatca) {
        getCrsAndFatca(baseInformation?.questionnaire?.id as string);
      }
    }
  }, [
    baseInformation,
    currentApplicationScheme,
    accountsCurrencies,
    accountsFlows,
    crsAndFatca,
  ]);

  useEffect(() => {
    if (
      baseInformation &&
      baseInformation?.questionnaire &&
      currentApplicationScheme &&
      Boolean(
        currentApplicationScheme?.id === 'a87db4b4-3c78-4564-bba1-c16a19553159',
      )
    ) {
      if (!crsAndFatca) {
        getCOFCrsAndFatca(baseInformation?.questionnaire?.id as string);
      }

      if (!affiliatedCompanies) {
        getAffilatedCompanies(baseInformation?.questionnaire?.id as string);
      }
    }
  }, [
    baseInformation,
    currentApplicationScheme,
    affiliatedCompanies,
    crsAndFatca,
  ]);

  useEffect(() => {
    if (
      currentApplicationScheme &&
      (currentApplicationScheme?.id ===
        'ed560559-8354-4634-b6dd-eb341dfb551a' ||
        currentApplicationScheme?.id ===
          'a87db4b4-3c78-4564-bba1-c16a19553159') &&
      baseInformation
    ) {
      const body = {
        birthCountryId:
          (baseInformation as Partial<GeneralInfo>)?.birthCountryId || '',
        placeOfBirth:
          (baseInformation as Partial<GeneralInfo>)?.placeOfBirth || '',
        nameOfEmployer:
          (baseInformation as Partial<GeneralInfo>)?.nameOfEmployer || '',
        employeePosition:
          (baseInformation as Partial<GeneralInfo>)?.employeePosition || '',
        countryOfEmployerId:
          (baseInformation as Partial<GeneralInfo>)?.countryOfEmployerId || '',
        correspondenceAddress1:
          (baseInformation as Partial<GeneralInfo>)?.correspondenceAddress1 ||
          '',
        correspondenceAddress2:
          (baseInformation as Partial<GeneralInfo>)?.correspondenceAddress2 ||
          '',
        correspondenceAddressSameAsRegisteredAddress: (
          baseInformation as Partial<GeneralInfo>
        )?.correspondenceAddressSameAsRegisteredAddress,
        correspondenceCity:
          (baseInformation as Partial<GeneralInfo>)?.correspondenceCity || '',
        correspondenceCountryId:
          (baseInformation as Partial<GeneralInfo>)?.correspondenceCountryId ||
          '',
        correspondencePostalCode:
          (baseInformation as Partial<GeneralInfo>)?.correspondencePostalCode ||
          '',
        typeOfActivityDescription:
          (baseInformation as Partial<GeneralInfo>)
            ?.typeOfActivityDescription || '',
        clientActivityId:
          (baseInformation as Partial<GeneralInfo>)?.clientActivityId || '',
        isUSResident:
          currentApplicationScheme?.id ===
            'ed560559-8354-4634-b6dd-eb341dfb551a' &&
          (baseInformation as Partial<GeneralInfo>)?.isUSResident?.toString() &&
          (
            baseInformation as Partial<GeneralInfo>
          )?.isUSResident?.toString() !== 'null'
            ? (baseInformation as Partial<GeneralInfo>)?.isUSResident === false
              ? 'no'
              : 'yes'
            : '',
      };

      organizationReset(body);

      setDefaultOrganizationValues(body);
    }
  }, [baseInformation, currentApplicationScheme]);

  useEffect(() => {
    if (
      (activePermissions?.includes(PermissionEnum.DICTIONARIES_ITEMS_LIST) &&
        currentApplicationScheme &&
        Boolean(
          currentApplicationScheme?.id ===
          'ed560559-8354-4634-b6dd-eb341dfb551a',
        )) ||
      Boolean(
        currentApplicationScheme?.id === 'a87db4b4-3c78-4564-bba1-c16a19553159',
      )
    ) {
      if (!sourceOfIncome) {
        getCustomersSourceOfIncome();
      }

      if (!sourceOfWealth) {
        getCustomersSourceOfWealth();
      }

      if (!sourceOfWealthForIndividual) {
        getCustomersSourceOfWealthForIndividua();
      }

      if (!customerSourceOfFunds) {
        getCustomersSourceOfFundsDictionary();
      }
    }
  }, [currentApplicationScheme, activePermissions]);

  useEffect(() => {
    if (
      baseInformation &&
      baseInformation?.questionnaire &&
      currentApplicationScheme &&
      Boolean(
        currentApplicationScheme?.id === 'cdbb8c71-90e6-4a5e-964f-e55169e0c4df',
      )
    ) {
      if (!accountsCurrencies) {
        getAccountsCurrencies(baseInformation?.questionnaire?.id as string);
      }

      if (!accountsFlows) {
        getAccountsFlows(baseInformation?.questionnaire?.id as string);
      }

      if (!expectedFundsOrigins) {
        getExpectedFundsOrigins(baseInformation?.questionnaire?.id as string);
      }
    }
  }, [
    baseInformation,
    currentApplicationScheme,
    accountsCurrencies,
    accountsFlows,
  ]);

  useEffect(() => {
    if (
      activePermissions?.includes(PermissionEnum.DICTIONARIES_ITEMS_LIST) &&
      currentApplicationScheme &&
      Boolean(
        currentApplicationScheme?.id === 'cdbb8c71-90e6-4a5e-964f-e55169e0c4df',
      )
    ) {
      if (!relationshipToTheCustomerDictionary) {
        getRelationShipToCustomerDictionary();
      }

      if (!sourceOfWealth) {
        getSourceOfWealthForCorporateDictionary();
      }

      if (!businessActivities) {
        getBusinessActivitiesDictionary();
      }

      if (!customerMainClients) {
        getRegulatedCompanyMainClientsDictionary();
      }

      if (!salesChannels) {
        getSalesChannelsDictionary();
      }

      if (!customerSourceOfFunds) {
        getCustomersSourceOfFundsDictionary();
      }

      if (!customerSourceOfWealth) {
        getCustomersSourceOfWealth();
      }

      if (!annualIncomes) {
        getCustomersAnnualIncomes();
      }

      if (!typeOfAccounts) {
        getCustomersTypeOfAccounts();
      }
    }
  }, [currentApplicationScheme, activePermissions]);

  useEffect(() => {
    if (baseInformation?.questionnaire && type === OrganizationType.CORPORATE) {
      let body = {
        ...baseInformation?.questionnaire,
      };

      body = {
        ...body,
        hasTransactionMonitoring: baseInformation?.questionnaire
          .hasTransactionMonitoring
          ? 'yes'
          : 'no',
        hasScreening: baseInformation?.questionnaire.hasScreening
          ? 'yes'
          : 'no',
        hasRegulatoryInvestigation: baseInformation?.questionnaire
          .hasRegulatoryInvestigation
          ? 'yes'
          : 'no',
        hasOnboardCorporateClients: baseInformation?.questionnaire
          .hasOnboardCorporateClients
          ? 'yes'
          : 'no',
        hasOnboardMsb: baseInformation?.questionnaire.hasOnboardMsb
          ? 'yes'
          : 'no',
        hasPaymentsForCharities: baseInformation?.questionnaire
          .hasPaymentsForCharities
          ? 'yes'
          : 'no',
        hasChangesInManagementOrOwnership: baseInformation?.questionnaire
          .hasChangesInManagementOrOwnership
          ? 'yes'
          : 'no',
        hasWorkingWithAnyOtherIpsp: baseInformation?.questionnaire
          .hasWorkingWithAnyOtherIpsp
          ? 'yes'
          : 'no',
        hasAuditedByExternalCompliance: baseInformation?.questionnaire
          .hasWorkingWithAnyOtherIpsp
          ? 'yes'
          : 'no',
        isPepOrAssociate:
          baseInformation?.questionnaire?.isPepOrAssociate?.toString() &&
          baseInformation?.questionnaire?.isPepOrAssociate?.toString() !==
            'null'
            ? baseInformation?.questionnaire?.isPepOrAssociate === false
              ? 'no'
              : 'yes'
            : '',
        abilityMoveGovernmentFunds:
          baseInformation?.questionnaire?.abilityMoveGovernmentFunds?.toString() &&
          baseInformation?.questionnaire?.abilityMoveGovernmentFunds?.toString() !==
            'null'
            ? baseInformation?.questionnaire?.abilityMoveGovernmentFunds ===
              false
              ? 'no'
              : 'yes'
            : '',
        belongsToGroup:
          baseInformation?.questionnaire?.belongsToGroup?.toString() &&
          baseInformation?.questionnaire?.belongsToGroup?.toString() !== 'null'
            ? baseInformation?.questionnaire?.belongsToGroup === false
              ? 'no'
              : 'yes'
            : '',
        estimatedTotalWealthEuro:
          baseInformation?.questionnaire?.estimatedTotalWealthEuro ||
          baseInformation?.questionnaire?.estimatedTotalWealthEuro?.toString() ===
            '0'
            ? baseInformation?.questionnaire?.estimatedTotalWealthEuro?.toString()
            : '',
        customerAppliesKycProcedures:
          baseInformation?.questionnaire?.customerAppliesKycProcedures?.toString() &&
          baseInformation?.questionnaire?.customerAppliesKycProcedures?.toString() !==
            'null'
            ? baseInformation?.questionnaire?.customerAppliesKycProcedures ===
              false
              ? 'no'
              : 'yes'
            : '',
        hasBusinessActivity:
          baseInformation?.questionnaire?.hasBusinessActivity?.toString() &&
          baseInformation?.questionnaire?.hasBusinessActivity?.toString() !==
            'null'
            ? baseInformation?.questionnaire?.hasBusinessActivity === false
              ? 'no'
              : 'yes'
            : '',
        businessActivitiesIds:
          baseInformation?.questionnaire?.hasBusinessActivity?.toString() &&
          baseInformation?.questionnaire?.hasBusinessActivity?.toString() !==
            'null' &&
          baseInformation?.questionnaire?.hasBusinessActivity === false
            ? ['NONE']
            : baseInformation?.questionnaire?.businessActivitiesIds,
        numberOfEmployees:
          baseInformation?.questionnaire?.numberOfEmployees ||
          baseInformation?.questionnaire?.numberOfEmployees?.toString() === '0'
            ? baseInformation?.questionnaire?.numberOfEmployees.toString()
            : '',
        hasAccountsInOtherFinancialInstitutions:
          baseInformation?.questionnaire?.hasAccountsInOtherFinancialInstitutions?.toString() &&
          baseInformation?.questionnaire?.hasAccountsInOtherFinancialInstitutions?.toString() !==
            'null'
            ? baseInformation?.questionnaire
                ?.hasAccountsInOtherFinancialInstitutions === false
              ? 'no'
              : 'yes'
            : '',
        estimatedTotalWealth:
          baseInformation?.questionnaire?.estimatedTotalWealth ||
          baseInformation?.questionnaire?.estimatedTotalWealth?.toString() ===
            '0'
            ? baseInformation?.questionnaire?.estimatedTotalWealth?.toString()
            : '',
        annualIncome:
          baseInformation?.questionnaire?.annualIncome ||
          baseInformation?.questionnaire?.annualIncome?.toString() === '0'
            ? baseInformation?.questionnaire?.annualIncome?.toString()
            : '',
      };

      reset(body);

      setDefaultQuestionaire(body);
    }
  }, [baseInformation, type]);

  useEffect(() => {
    if (baseInformation?.questionnaire && type !== OrganizationType.CORPORATE) {
      const body = {
        ...baseInformation?.questionnaire,
        criminalConvictedOfAny:
          baseInformation?.questionnaire?.criminalConvictedOfAny?.toString() &&
          baseInformation?.questionnaire?.criminalConvictedOfAny?.toString() !==
            'null'
            ? baseInformation?.questionnaire?.criminalConvictedOfAny === false
              ? 'no'
              : 'yes'
            : '',
        criminalReceivedACautionForAny:
          baseInformation?.questionnaire?.criminalReceivedACautionForAny?.toString() &&
          baseInformation?.questionnaire?.criminalReceivedACautionForAny?.toString() !==
            'null'
            ? baseInformation?.questionnaire?.criminalReceivedACautionForAny ===
              false
              ? 'no'
              : 'yes'
            : '',
        criminalArrestedOrChargedWithOrBeenInvolved:
          baseInformation?.questionnaire?.criminalArrestedOrChargedWithOrBeenInvolved?.toString() &&
          baseInformation?.questionnaire?.criminalArrestedOrChargedWithOrBeenInvolved?.toString() !==
            'null'
            ? baseInformation?.questionnaire
                ?.criminalArrestedOrChargedWithOrBeenInvolved === false
              ? 'no'
              : 'yes'
            : '',
        criminalSubjectToAnyCurrentCriminalInvestigation:
          baseInformation?.questionnaire?.criminalSubjectToAnyCurrentCriminalInvestigation?.toString() &&
          baseInformation?.questionnaire?.criminalSubjectToAnyCurrentCriminalInvestigation?.toString() !==
            'null'
            ? baseInformation?.questionnaire
                ?.criminalSubjectToAnyCurrentCriminalInvestigation === false
              ? 'no'
              : 'yes'
            : '',
        criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenConvicted:
          baseInformation?.questionnaire?.criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenConvicted?.toString() &&
          baseInformation?.questionnaire?.criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenConvicted?.toString() !==
            'null'
            ? baseInformation?.questionnaire
                ?.criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenConvicted ===
              false
              ? 'no'
              : 'yes'
            : '',
        criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenOrdered:
          baseInformation?.questionnaire.criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenOrdered?.toString() &&
          baseInformation?.questionnaire.criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenOrdered?.toString() !==
            'null'
            ? baseInformation?.questionnaire
                ?.criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenOrdered ===
              false
              ? 'no'
              : 'yes'
            : '',
        criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenInvolved:
          baseInformation?.questionnaire?.criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenInvolved?.toString() &&
          baseInformation?.questionnaire?.criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenInvolved?.toString() !==
            'null'
            ? baseInformation?.questionnaire
                ?.criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenInvolved ===
              false
              ? 'no'
              : 'yes'
            : '',
        civilFoundLiableForFraud:
          baseInformation?.questionnaire?.civilFoundLiableForFraud?.toString() &&
          baseInformation?.questionnaire?.civilFoundLiableForFraud?.toString() !==
            'null'
            ? baseInformation?.questionnaire?.civilFoundLiableForFraud === false
              ? 'no'
              : 'yes'
            : '',
        civilSubjectToAJudgmentDebt:
          baseInformation?.questionnaire?.civilSubjectToAJudgmentDebt?.toString() &&
          baseInformation?.questionnaire?.civilSubjectToAJudgmentDebt?.toString() !==
            'null'
            ? baseInformation?.questionnaire?.civilSubjectToAJudgmentDebt ===
              false
              ? 'no'
              : 'yes'
            : '',
        civilInvolvedInAnyCivilProceedings:
          baseInformation?.questionnaire?.civilInvolvedInAnyCivilProceedings?.toString() &&
          baseInformation?.questionnaire?.civilInvolvedInAnyCivilProceedings?.toString() !==
            'null'
            ? baseInformation?.questionnaire
                ?.civilInvolvedInAnyCivilProceedings === false
              ? 'no'
              : 'yes'
            : '',
        civilYouHeldAPositionOfInfluence:
          baseInformation?.questionnaire?.civilYouHeldAPositionOfInfluence?.toString() &&
          baseInformation?.questionnaire?.civilYouHeldAPositionOfInfluence?.toString() !==
            'null'
            ? baseInformation?.questionnaire
                ?.civilYouHeldAPositionOfInfluence === false
              ? 'no'
              : 'yes'
            : '',
        civilFiledForBankruptcy:
          baseInformation?.questionnaire?.civilFiledForBankruptcy?.toString() &&
          baseInformation?.questionnaire?.civilFiledForBankruptcy?.toString() !==
            'null'
            ? baseInformation?.questionnaire?.civilFiledForBankruptcy === false
              ? 'no'
              : 'yes'
            : '',
        civilSubjectToABankruptcy:
          baseInformation?.questionnaire?.civilSubjectToABankruptcy?.toString() &&
          baseInformation?.questionnaire?.civilSubjectToABankruptcy?.toString() !==
            'null'
            ? baseInformation?.questionnaire?.civilSubjectToABankruptcy ===
              false
              ? 'no'
              : 'yes'
            : '',
        businessAndEmploymentDismissedOrAskedTo:
          baseInformation?.questionnaire?.businessAndEmploymentDismissedOrAskedTo?.toString() &&
          baseInformation?.questionnaire?.businessAndEmploymentDismissedOrAskedTo?.toString() !==
            'null'
            ? baseInformation?.questionnaire
                ?.businessAndEmploymentDismissedOrAskedTo === false
              ? 'no'
              : 'yes'
            : '',
        businessAndEmploymentDisqualifiedFromActingAsADirector:
          baseInformation?.questionnaire?.businessAndEmploymentDisqualifiedFromActingAsADirector?.toString() &&
          baseInformation?.questionnaire?.businessAndEmploymentDisqualifiedFromActingAsADirector?.toString() !==
            'null'
            ? baseInformation?.questionnaire
                ?.businessAndEmploymentDisqualifiedFromActingAsADirector ===
              false
              ? 'no'
              : 'yes'
            : '',
        businessAndEmploymentTheSubjectOfAnyDisciplinaryProceedings:
          baseInformation?.questionnaire?.businessAndEmploymentTheSubjectOfAnyDisciplinaryProceedings?.toString() &&
          baseInformation?.questionnaire?.businessAndEmploymentTheSubjectOfAnyDisciplinaryProceedings?.toString() !==
            'null'
            ? baseInformation?.questionnaire
                ?.businessAndEmploymentTheSubjectOfAnyDisciplinaryProceedings ===
              false
              ? 'no'
              : 'yes'
            : '',
        regulatoryAnAuthorizationFromARegulatoryBodyRefused:
          baseInformation?.questionnaire?.regulatoryAnAuthorizationFromARegulatoryBodyRefused?.toString() &&
          baseInformation?.questionnaire?.regulatoryAnAuthorizationFromARegulatoryBodyRefused?.toString() !==
            'null'
            ? baseInformation?.questionnaire
                ?.regulatoryAnAuthorizationFromARegulatoryBodyRefused === false
              ? 'no'
              : 'yes'
            : '',
        regulatoryCensured:
          baseInformation?.questionnaire?.regulatoryCensured?.toString() &&
          baseInformation?.questionnaire?.regulatoryCensured?.toString() !==
            'null'
            ? baseInformation?.questionnaire?.regulatoryCensured === false
              ? 'no'
              : 'yes'
            : '',
        regulatoryInvolvedInAnyActivitiesRequiringAuthorization:
          baseInformation?.questionnaire?.regulatoryInvolvedInAnyActivitiesRequiringAuthorization?.toString() &&
          baseInformation?.questionnaire?.regulatoryInvolvedInAnyActivitiesRequiringAuthorization?.toString() !==
            'null'
            ? baseInformation?.questionnaire
                ?.regulatoryInvolvedInAnyActivitiesRequiringAuthorization ===
              false
              ? 'no'
              : 'yes'
            : '',
        regulatorySubjectToAnyDisqualification:
          baseInformation?.questionnaire?.regulatorySubjectToAnyDisqualification?.toString() &&
          baseInformation?.questionnaire?.regulatorySubjectToAnyDisqualification?.toString() !==
            'null'
            ? baseInformation?.questionnaire
                ?.regulatorySubjectToAnyDisqualification === false
              ? 'no'
              : 'yes'
            : '',
        ownership: baseInformation?.questionnaire?.ownership?.toString() || '',
        belongsToGroup:
          baseInformation?.questionnaire?.belongsToGroup?.toString() &&
          baseInformation?.questionnaire?.belongsToGroup?.toString() !== 'null'
            ? baseInformation?.questionnaire?.belongsToGroup === false
              ? 'no'
              : 'yes'
            : '',
        estimatedTotalWealthEuro:
          baseInformation?.questionnaire?.estimatedTotalWealthEuro ||
          baseInformation?.questionnaire?.estimatedTotalWealthEuro?.toString() ===
            '0'
            ? baseInformation?.questionnaire?.estimatedTotalWealthEuro?.toString()
            : '',
        requiresSourcesOfIncomes:
          baseInformation?.questionnaire?.requiresSourcesOfIncomes?.toString() &&
          baseInformation?.questionnaire?.requiresSourcesOfIncomes?.toString() !==
            'null'
            ? baseInformation?.questionnaire?.requiresSourcesOfIncomes === false
              ? 'no'
              : 'yes'
            : '',
        requiresSourceAndSizeOfWealth:
          baseInformation?.questionnaire?.requiresSourceAndSizeOfWealth?.toString() &&
          baseInformation?.questionnaire?.requiresSourceAndSizeOfWealth?.toString() !==
            'null'
            ? baseInformation?.questionnaire?.requiresSourceAndSizeOfWealth ===
              false
              ? 'no'
              : 'yes'
            : '',
        requiresNetWorth:
          baseInformation?.questionnaire?.requiresNetWorth?.toString() &&
          baseInformation?.questionnaire?.requiresNetWorth?.toString() !==
            'null'
            ? baseInformation?.questionnaire?.requiresNetWorth === false
              ? 'no'
              : 'yes'
            : '',
        isPepOrAssociate:
          baseInformation?.questionnaire?.isPepOrAssociate?.toString() &&
          baseInformation?.questionnaire?.isPepOrAssociate?.toString() !==
            'null'
            ? baseInformation?.questionnaire?.isPepOrAssociate === false
              ? 'no'
              : 'yes'
            : '',
        hasAffiliatedCompanies:
          baseInformation?.questionnaire?.hasAffiliatedCompanies?.toString() &&
          baseInformation?.questionnaire?.hasAffiliatedCompanies?.toString() !==
            'null'
            ? baseInformation?.questionnaire?.hasAffiliatedCompanies === false
              ? 'no'
              : 'yes'
            : '',
        abilityMoveGovernmentFunds:
          baseInformation?.questionnaire?.abilityMoveGovernmentFunds?.toString() &&
          baseInformation?.questionnaire?.abilityMoveGovernmentFunds?.toString() !==
            'null'
            ? baseInformation?.questionnaire?.abilityMoveGovernmentFunds ===
              false
              ? 'no'
              : 'yes'
            : '',
        isUSCitizen:
          baseInformation?.questionnaire?.isUSCitizen?.toString() &&
          baseInformation?.questionnaire?.isUSCitizen?.toString() !== 'null'
            ? baseInformation?.questionnaire?.isUSCitizen === false
              ? 'no'
              : 'yes'
            : '',
        hasAccountsInOtherFinancialInstitutions:
          baseInformation?.questionnaire?.hasAccountsInOtherFinancialInstitutions?.toString() &&
          baseInformation?.questionnaire?.hasAccountsInOtherFinancialInstitutions?.toString() !==
            'null'
            ? baseInformation?.questionnaire
                ?.hasAccountsInOtherFinancialInstitutions === false
              ? 'no'
              : 'yes'
            : '',
      };

      reset(body);

      setDefaultQuestionaire(body);
    }
  }, [baseInformation, type]);

  return {
    setOngoingItemPanelVisible,
    onOngoingRowClick,
    ongoingItemIsFetching,
    ongoingItemIsLoading,
    setBlacklistDataVisible,
    setBlacklistData,
    setBlacklistDataItem,
    setBlacklistItemVisible,
    applyError,
    applyWarningInProgress,
    applyCustomerOngoingWarningAction,
    batchApplyWarningInProgress,
    batchApplyCustomerOngoingWarningAction,
    batchApplyWarningIsSuccess,
    onResetTransactionWarningAction,
    resetTransactionsWarningIsLoading,
    resetTransactionsWarningIsSuccess,
    selectedWarningId,
    selectedWarningGroup,
    getCustomerWarningsAction,
    paginationInProgress,
    paginationIsFetching,
    getCustomerOngoingWarningByIdAction,
    isWarningByIdLoading,
    isWarningByIdFetching,
    showOngoing,
    setShowOngoing,
    ongoingIsLoading,
    ongoingIsFetching,
    getCustomerOngoingMonitoring,
    setExportPdfPanelVisible,
    setChangeLogPanelIsOpen,
    questionnaireFields,
    control,
    currentApplicationScheme,
    defaultQuestionnaire,
    onResetQuestionnaire,
    onUpdateQuestionnaire,
    questionnnaireRequestInProgress,
    setValue,
    isLoading,
    onUpdate,
    requestInProgress,
    isSuccess,
    setRiskScoreChangeLogsSidepanelVisible,
    setManualScoreSidepanelVisible,
    cddFormValues,
    onCDDFormValuesChange,
    onSaveChanges,
    isDirty,
    updateRangesInProgress,
    successMessageVisible,
    exportPdfPanelVisible,
    ongoingMonitoringPdfIsLoading,
    getOngoingMonitoringPdfAction,
    exportDate,
    setExportDate,
    ongoingItemPanelVisible,
    setApplyError,
    blacklistDataVisible,
    blacklistData,
    blacklistItemVisible,
    blacklistItem,
    changeLogsPanelIsOpen,
    changeLogsIsLoading,
    changeLogsIsFetching,
    getChangeLogsAction,
    manualScoreSidepanelVisible,
    totalRiskScore,
    setTotalRiskScore,
    setManualRiskScoreError,
    setDirtyPopupVisible,
    manualRiskScoreError,
    createRiskScoreIsLoading,
    createCustomerManualRiskScoreAction,
    totalRiskScoreLevel,
    dirtyPopupVisible,
    riskScoreChangeLogsSidepanelVisible,
    getCustomerAmlChangeLogsAction,
    handleCloseAmlChangeLogsPanel,
    amlScoresHistoryIsFetching,
    amlScoresHistoryIsLoading,
    updateInfoIsLoading,
    setTotalRiskScoreLevel,
    defaultOrganizationValues,
    organizationFields,
    informationIsUpdate,
    errors,
    clearErrors,
    accountsCurrenciesIsLoading:
      accountsCurrenciesIsLoading || accountsIndividualCurrenciesIsLoading,
    accountsCurrenciesIsFetching:
      accountsCurrenciesIsFetching || accountsIndividualCurrenciesIsFetching,
    accountsCurrenciesValues,
    setAccountsCurrenciesValues,
    onDeleteAccountCurrencies,
    selectedAccountsCurrencyId,
    deleteAccountsCurrenciesInProgress:
      deleteAccountsCurrenciesInProgress ||
      deleteIndividualAccountsCurrenciesInProgress,
    setAccountsFlowsValues,
    accountsFlowsValues,
    accountsFlowsIsLoading:
      accountsFlowsIsLoading || accountsIndividualFlowsIsLoading,
    accountsFlowsIsFetching:
      accountsFlowsIsFetching || accountsIndividualFlowsIsFetching,
    expectedFundsOriginsIsLoading:
      expectedFundsOriginsIsLoading || expectedIndividualFundsOriginsIsLoading,
    expectedFundsOriginsIsFetching:
      expectedFundsOriginsIsFetching ||
      expectedIndividualFundsOriginsIsFetching,
    deleteExpectedFundsOriginsInProgress:
      deleteExpectedFundsOriginsInProgress ||
      deleteIndividualExpectedFundsOriginsInProgress,
    onDeleteExpectedFundsOrigins,
    selectedExpectedFundsOriginsId,
    expectedFundsOriginsValues,
    setExpectedFundsOriginsValues,
    createAccountsCurrenciesIsProgress:
      createAccountsCurrenciesIsProgress ||
      createIndividualAccountsCurrenciesIsProgress,
    updateAccountsCurrenciesInProgress:
      updateAccountsCurrenciesInProgress ||
      updateIndividualAccountsCurrenciesInProgress,
    createAccountsFlowsIsProgress:
      createAccountsFlowsIsProgress || createIndividualAccountsFlowsIsProgress,
    updateAccountsFlowsInProgress:
      updateAccountsFlowsInProgress || updateIndividualAccountsFlowsInProgress,
    createExpectedFundsOriginsInProgress:
      createExpectedFundsOriginsInProgress ||
      createIndividualExpectedFundsOriginsInProgress,
    updateExpectedFundsOriginsInProgress:
      updateExpectedFundsOriginsInProgress ||
      updateIndividualExpectedFundsOriginsInProgress,
    selectedCrsAndFatcaId,
    crsAndFatcaIsLoading: crsAndFatcaIsLoading || cofCrsAndFatcaIsLoading,
    crsAndFatcaIsFetching: crsAndFatcaIsFetching || cofCrsAndFatcaIsFetching,
    createCrsAndFatcaIsProgress:
      createCrsAndFatcaIsProgress || createCofCrsAndFatcaIsProgress,
    updateCrsAndFatcaInProgress:
      updateCrsAndFatcaInProgress || updateCofCrsAndFatcaInProgress,
    deleteCrsAndFatcaInProgress:
      deleteCrsAndFatcaInProgress || deleteCofCrsAndFatcaInProgress,
    onDeleteCRSAndFatca,
    organizationControl,
    organizationErrors,
    organizationClearErrors,
    organizationSetValue,
    crsAndFatcaValues,
    setCrsAndFatcaValues,
    affilatedCompaniesValues,
    setAffilatedCompaniesValues,
    selectedAffilatedCompanyId,
    affilatedCompaniesIsLoading,
    affilatedCompaniesIsFetching,
    createAffilatedCompanyInProgress,
    updateAffilatedCompanyInProgress,
    deleteAffilatedCompanyInProgress,
    onDeleteAffilatedCompany,
    totalIncomeError,
    setTotalIncomeError,
  };
};

export default useAML;
